// Fuzzball Quiz Controller
// Main quiz flow logic and coordination

const VET_OVERRIDE_KEYS = {
  // Slide 2 — food preference
  'wet_dry_preference': { 'Wet': 'vetWet', 'Dry': 'vetDry', 'Both': 'vetBoth' },
  // Slide 3 — quantity
  'cans_per_day': { '1': 'vetText-1', '2': 'vetText-2', '3': 'vetText-3' },
  // Slide 4 — health concerns
  'health_concerns_exists': { 'Yes': 'vetTextHealthYes', 'No': 'vetTextHealthNo' },
  // Slide 4 — lifestyle
  'lifestyle': { 'Indoor': 'vetTextIndoor', 'Outdoor': 'vetTextOutdoor', 'Both': 'vetTextBothLifestyle' }
};

const VET_TITLE_OVERRIDE_KEYS = {
  // Slide 2 — food preference
  'wet_dry_preference': { 'Wet': 'vetTitleWet', 'Dry': 'vetTitleDry', 'Both': 'vetTitleBoth' },
  // Slide 3 — quantity
  'cans_per_day': { '1': 'vetTitle-1', '2': 'vetTitle-2', '3': 'vetTitle-3' },
  // Slide 4 — health concerns
  'health_concerns_exists': { 'Yes': 'vetTitleHealthYes', 'No': 'vetTitleHealthNo' },
  // Slide 4 — lifestyle
  'lifestyle': { 'Indoor': 'vetTitleIndoor', 'Outdoor': 'vetTitleOutdoor', 'Both': 'vetTitleBothLifestyle' }
};

// Static step names for GA4 events. Decoupled from quiz copy so Jacob can
// edit headings in the theme editor without breaking analytics dimensions.
const QUIZ_STEP_NAMES = {
  1: 'cat_basics',
  2: 'food_preference',
  3: 'cans_per_day',
  4: 'health_lifestyle',
  5: 'flavour_dislikes',
  6: 'email_capture',
  7: 'plan_summary'
};

// gtag is loaded by a third-party Shopify integration (Gropulse) on the live
// storefront — may be absent in dev/preview. Swallow errors so analytics
// never breaks the quiz flow.
function quizGtagPush() {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag.apply(window, arguments);
    }
  } catch (e) {}
}

window.QuizController = class {
  constructor() {
    this.currentStep = 1;
    this.previousStep = null;
    this.totalSteps = 7;
    this.extrasPopupShown = false;
    this.skippedQuiz = false;
    this.submissionData = window.QuizState.get('submissionData') || {};
    this.visitedSteps = new Set([1]); // Track which steps have been visited for first-time scroll behavior
    this.scrollTimer = null; // Timer for debouncing auto-scroll
    this.quizStartedFired = false; // GA4: ensure quiz_started fires once per page session
    this.quizCompletedFired = false; // GA4: ensure quiz_completed fires once per page session
    this.entryMode = 'fresh'; // GA4: how this session entered — 'fresh' | 'byb' | 'edit'. Set in init(). Gates quiz_started/quiz_completed so edit re-entries don't inflate the funnel.
    
    // Always clear extras on page refresh/load to ensure fresh product data
    localStorage.removeItem('selectedExtras');
    localStorage.removeItem('step10Selections');
    window.QuizState.clear(['selectedExtras']);
    
    // Initialize first cat if not present
    if (!this.submissionData.cat1) {
      this.submissionData.cat1 = {
        fuzzballer_name: '',
        breed: '',
        age_range: '',
        wet_dry_preference: '',
        cans_per_day: '',
        health_concerns_exists: '',
        health_concerns: '',
        lifestyle: '',
        disliked_flavors: []
      };
      this.submissionData.catCount = 1;
      // Save the initial state
      window.QuizState.save('submissionData', this.submissionData);
    }
    
    // Always ensure catCount exists and is at least 1
    if (!this.submissionData.catCount || this.submissionData.catCount < 1) {
      this.submissionData.catCount = 1;
      window.QuizState.save('submissionData', this.submissionData);
    }
    
    // quiz-bundle fires selectionsUpdated (non-bubbling) on every add/remove in
    // the Edit monthly plan pop-up; listen in the capture phase so the free
    // shipping bars move live while the customer edits.
    document.addEventListener('selectionsUpdated', () => this.updateFreeShippingBar(), true);

    this.init();
  }

  // Prefer the live variant-priced total from quiz-bundle when it has
  // populated selections; otherwise fall back to the estimate from
  // QuizCalculator (lower-bound, Liquid-injected defaults).
  getLiveOrEstimatedBundlePrice(recommendations, catKey) {
    const bundleElement = document.querySelector('quiz-bundle');
    if (bundleElement && bundleElement.selections) {
      let items = [];
      if (catKey && Array.isArray(bundleElement.selections[catKey])) {
        items = bundleElement.selections[catKey];
      } else if (!catKey) {
        items = Object.values(bundleElement.selections).flat();
      }
      if (items.length > 0) {
        return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      }
    }
    if (recommendations && recommendations.cats && !catKey) {
      return window.QuizCalculator.calculateMultiCatBundlePrice(recommendations);
    }
    return window.QuizCalculator.calculateBundlePrice(recommendations);
  }

  init() {

    // Check if user is returning from checkout via browser back button
    // If they already submitted and went to checkout, redirect them to cart
    if (this.checkPendingCheckout()) {
      return; // Stop init if redirecting
    }

    // Handle bfcache - when page is restored from cache, re-check
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        console.log('[Quiz Debug] Page restored from bfcache, re-checking...');
        this.checkPendingCheckout();
      }
    });

    // Check for edit mode from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const editMode = urlParams.get('mode') === 'edit';
    
    if (editMode) {
      this.entryMode = 'edit'; // GA4: mid-flow edit re-entry — excluded from start/completion counts
      console.log('URL params:', {
        mode: urlParams.get('mode'),
        quiz_id: urlParams.get('quiz_id'),
        edit_type: urlParams.get('edit_type'),
        cat: urlParams.get('cat')
      });

      this.handleEditMode(urlParams);

      // Strip edit params so browser back doesn't re-enter edit mode
      const cleanUrl = new URL(window.location);
      cleanUrl.searchParams.delete('mode');
      cleanUrl.searchParams.delete('edit_type');
      cleanUrl.searchParams.delete('quiz_id');
      cleanUrl.searchParams.delete('cat');
      window.history.replaceState({}, '', cleanUrl);
    } else {
      window.QuizUtils.DOM.lockBodyScroll();
      
      // Clear quiz completion flag when starting fresh from step 1
      if (this.currentStep === 1) {
        window.QuizState.clear(['quiz_submitted', 'selectedExtras']);
        // Also clear unified step 10 selections
        localStorage.removeItem('step10Selections');
        this.extrasPopupShown = false;

        // Fix corrupted catCount data
        const currentData = window.QuizState.get('submissionData');
        if (currentData && (currentData.catCount === 0 || !currentData.catCount)) {
          currentData.catCount = 1;
          window.QuizState.save('submissionData', currentData);
        }
      }
    }
    
    this.loadExistingData();

    // Homepage BYB entry (?byb=1): tag the session for GA4 only. The quiz still
    // starts on step 1 with the homepage name pre-filled, so multi-cat owners see
    // Add Another Cat (it only shows on step 1). Skipping to step 2 was removed
    // 19 Oct 2026 at Jacob's request.
    const cameFromByb = urlParams.get('byb') === '1';
    if (this.currentStep === 1 && cameFromByb && this.submissionData?.cat1?.fuzzballer_name && !editMode) {
      this.entryMode = 'byb'; // GA4: genuine fresh start via homepage BYB CTA
    }

    // Log stored quiz data on page load
    console.log('📊 Quiz data on page load:', this.submissionData);

    this.setupEventListeners();
    this.setupConditionalLogic();
    this.initializeCatGreyAnimations();
    
    // Populate form after event listeners are set up
    if (window.QuizState.load('submissionData')) {
      this.populateForm();
    }
    
    // Check if we should open skip popup (from Build Your Own button)
    if (localStorage.getItem('openSkipPopup') === 'true') {
      localStorage.removeItem('openSkipPopup'); // Clear the flag
      
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        // Determine which fields are missing for skip popup
        const requiredFields = ['fuzzballer_name', 'wet_dry_preference'];
        const missingFields = requiredFields.filter(field => 
          !this.submissionData.cat1[field] || this.submissionData.cat1[field] === ''
        );
        
        this.showSkipDataPopup(missingFields);
      }, 500);
    }
    
    // If we're on step 7, ensure trail box counts are updated after everything loads
    if (this.currentStep === 7 && (this.submissionData.catCount || 1) === 1) {
      
      // Use multiple timeouts to ensure trail box is ready
      [300, 600, 1000].forEach(delay => {
        setTimeout(() => {
          const trailBoxElement = document.querySelector('trail-box');
          if (trailBoxElement && trailBoxElement.selections && trailBoxElement.selections.cat1) {
            const totalItems = trailBoxElement.selections.cat1.reduce((sum, item) => sum + item.quantity, 0);
            
            // Update all trail box count displays
            const allTrailBoxCounts = document.querySelectorAll('.trail-box-items-count');
            allTrailBoxCounts.forEach(element => {
              if (!element.closest('[data-cat]')) { // Skip multi-cat elements
                element.textContent = totalItems;
              }
            });
          }
        }, delay);
      });
    }
    
    // Set initial step data attribute
    const quizContainer = document.querySelector('.fuzzball-quiz-container');
    if (quizContainer) {
      quizContainer.setAttribute('data-current-step', this.currentStep);
    }
    
    this.updateStepDisplay();
    this.updateProgressDisplay();
    this.setupAddCatButton();
    
    // Ensure add cat button visibility is correct after initial setup
    const addCatBtn = document.querySelector('.add-another-cat');
    if (addCatBtn && this.currentStep === 1 && (this.submissionData.catCount || 1) < 3) {
      addCatBtn.style.display = 'block';
    }
    
    // Note: updateStepDisplay already handles the initial scroll, so we don't need to do it again here
  }

  // Check if user is returning from checkout via browser back button
  // Returns true if redirecting (caller should stop execution)
  checkPendingCheckout() {
    console.log('[Quiz Debug] Checking for pending checkout...');
    console.log('[Quiz Debug] quiz_pending_checkout value:', localStorage.getItem('quiz_pending_checkout'));

    const pendingCheckout = localStorage.getItem('quiz_pending_checkout');
    if (pendingCheckout) {
      const checkoutTime = parseInt(pendingCheckout, 10);
      const thirtyMinutes = 30 * 60 * 1000;
      const now = Date.now();

      console.log('[Quiz Debug] Parsed checkout time:', checkoutTime);
      console.log('[Quiz Debug] Current time:', now);
      console.log('[Quiz Debug] Time difference (ms):', now - checkoutTime);
      console.log('[Quiz Debug] Within 30 min window:', (now - checkoutTime) < thirtyMinutes);

      // If within 30 minutes of checkout redirect, send them to cart
      if (now - checkoutTime < thirtyMinutes) {
        console.log('[Quiz Debug] REDIRECTING TO CART - user returned from checkout');
        // Clear the flag so they can start fresh if they want
        localStorage.removeItem('quiz_pending_checkout');
        window.location.href = '/cart';
        return true;
      } else {
        console.log('[Quiz Debug] Flag is stale (>30 mins), removing');
        // Stale flag, remove it
        localStorage.removeItem('quiz_pending_checkout');
      }
    } else {
      console.log('[Quiz Debug] No pending checkout flag found');
    }
    return false;
  }

  loadExistingData() {
    const saved = window.QuizState.load('submissionData');
    
    if (saved) {
      // Preserve the initialized cat1 if saved data doesn't have it
      const initialCat1 = this.submissionData.cat1;
      this.submissionData = { ...this.submissionData, ...saved };
      
      // If saved data didn't have cat1, use the initialized one
      if (!saved.cat1 && initialCat1) {
        this.submissionData.cat1 = initialCat1;
      }
      
      // Fix catCount if it's 0 or missing but we have cat data
      if (!this.submissionData.catCount || this.submissionData.catCount === 0) {
        // Count how many cats we actually have
        let actualCatCount = 0;
        for (let i = 1; i <= 3; i++) {
          if (this.submissionData[`cat${i}`]) {
            actualCatCount = i;
          }
        }
        this.submissionData.catCount = actualCatCount || 1;
        window.QuizState.save('submissionData', this.submissionData);
      }
      
      // Ensure we have the right cat count
      if (this.submissionData.catCount && this.submissionData.catCount > 1) {
        // Duplicate steps for existing cats
        for (let i = 2; i <= this.submissionData.catCount; i++) {
          this.duplicateStepsForCat(i);
        }
        
        // Update add cat button visibility
        if (this.submissionData.catCount >= 3) {
          const addCatBtn = document.querySelector('.add-another-cat');
          if (addCatBtn) addCatBtn.style.display = 'none';
        }
      }
    }
  }

  populateForm() {
    // Populate form fields with saved data
    const catCount = this.submissionData.catCount || 1;
    
    // Populate cat-specific data
    for (let i = 1; i <= catCount; i++) {
      const catData = this.submissionData[`cat${i}`];
      if (!catData) continue;
      
      Object.keys(catData).forEach(key => {
        // Find elements for this specific cat
        let elements;
        if (i === 1) {
          // For cat 1, look for original field names (no prefix)
          elements = document.querySelectorAll(`[name="${key}"]:not([data-cat])`);
        } else {
          // For other cats, look for data-cat attribute or prefixed names
          elements = document.querySelectorAll(`[data-original-name="${key}"][data-cat="${i}"], [name="cat${i}_${key}"]`);
        }
        
        elements.forEach(element => {
          const value = catData[key];
          if (value !== '' && value !== null && value !== undefined) {
            if (element.type === 'radio') {
              if (element.value === value) {
                element.checked = true;
              }
            } else if (element.type === 'checkbox') {
              // Handle disliked_flavors array
              if (Array.isArray(value) && value.includes(element.value)) {
                element.checked = true;
              }
            } else {
              element.value = value;
            }
          }
        });
      });
      
      // Update cat name displays if name exists
      if (catData.fuzzballer_name) {
        this.updateCatNameDisplaysForCat(catData.fuzzballer_name, i);
      }
      
    }
    
    // Populate global data (customer info)
    ['customer_name', 'customer_email'].forEach(key => {
      const element = document.querySelector(`[name="${key}"]`);
      if (element && this.submissionData[key]) {
        element.value = this.submissionData[key];
      }
    });
  }

  setupEventListeners() {
    // Form input listeners for real-time updates
    const form = document.querySelector('.quiz-form');
    if (form) {
      form.addEventListener('input', (e) => this.handleInput(e));
      form.addEventListener('change', (e) => this.handleInput(e));
    }
    
    // Prevent closing modals by clicking overlay
    const overlay = document.querySelector('.quiz-modal-overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        // Only handle clicks directly on the overlay, not its children
        if (e.target === overlay) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }

    // Navigation listeners
    document.addEventListener('click', (e) => {
      // Clear button
      if (e.target.closest('.quiz-nav-clear-btn')) {
        e.preventDefault();
        this.clearQuizData();
      }
      // Top navigation buttons
      else if (e.target.closest('.quiz-nav-back')) {
        this.prevStep();
      }
      // Top-bar forward arrow — mirror the bottom .quiz-next-btn so every
      // special case (step-6 Klaviyo submit, data-action='complete', standard
      // next) is reused exactly with no duplicate validation path. Route via a
      // programmatic click on the bottom next-btn rather than calling
      // nextStep() directly so the same disabled-state gate applies.
      else if (e.target.closest('.quiz-nav-forward')) {
        const forwardBtn = e.target.closest('.quiz-nav-forward');
        if (forwardBtn.disabled) return;
        const bottomNextBtn = document.querySelector('.quiz-next-btn');
        if (bottomNextBtn && !bottomNextBtn.disabled) {
          bottomNextBtn.click();
        }
      }
      // Bottom navigation buttons
      else if (e.target.classList.contains('quiz-next-btn')) {
        const action = e.target.getAttribute('data-action');
        if (action === 'complete') {
          this.completeQuiz();
        } else if (this.currentStep === 6) {
          // On step 6, submit to Klaviyo first
          e.preventDefault();
          this.handleStep6Submission();
        } else {
          this.nextStep();
        }
      } else if (e.target.classList.contains('quiz-skip-btn')) {
        this.skipToEnd();
      }
      // Step 6 custom button
      else if (e.target.closest('.step-6-button')) {
        e.preventDefault();
        this.handleStep6Submission();
      }
      // Step 7 edit recipes button (from .edit-recipes in meal plan)
      else if (e.target.closest('.edit-recipes')) {
        e.preventDefault();
        e.stopPropagation();
        
        // Get the cat number from the parent meal plan section if it exists
        const mealPlanSection = e.target.closest('.meal-plan-section');
        const catNumber = mealPlanSection ? parseInt(mealPlanSection.getAttribute('data-cat')) : null;
        
        this.openBundleSystem(catNumber);
      }
      // Toggle monthly products accordion
      else if (e.target.closest('.toggle-monthly-products')) {
        e.preventDefault();
        e.stopPropagation();
        
        const mealPlanSection = e.target.closest('.meal-plan-section');
        if (mealPlanSection) {
          const monthlyProductsSection = mealPlanSection.querySelector('.monthly-products-section');
          if (monthlyProductsSection) {
            monthlyProductsSection.classList.toggle('collapsed');
          }
        }
      }
      // Toggle trial products accordion
      else if (e.target.closest('.toggle-trial-products')) {
        e.preventDefault();
        e.stopPropagation();
        
        // For multi-cat, look within cat-container
        const catContainer = e.target.closest('.cat-container');
        if (catContainer) {
          const trialProductsSection = catContainer.querySelector('.trial-products-section');
          if (trialProductsSection) {
            trialProductsSection.classList.toggle('collapsed');
          }
        } else {
          // For single cat, look within the tab content
          const tabContent = e.target.closest('[data-tab-content="trial"]');
          if (tabContent) {
            const trialProductsSection = tabContent.querySelector('.trial-products-section');
            if (trialProductsSection) {
              trialProductsSection.classList.toggle('collapsed');
            }
          }
        }
      }
      // Trail box edit button - removed functionality
      // else if (e.target.closest('.trail-box-edit') || e.target.closest('.edit-trail-box')) {
      //   e.preventDefault();
      //   e.stopPropagation();
      //   
      //   // Get the cat number from the parent section
      //   const trailBoxSection = e.target.closest('.trail-box-section');
      //   const catNumber = trailBoxSection ? parseInt(trailBoxSection.getAttribute('data-cat')) : 1;
      //   
      //   this.openTrailBoxSystem(catNumber);
      // }
      
      // Edit trail box button (only from .edit-trail-box in items-info)
      else if (e.target.closest('.edit-trail-box')) {
        e.preventDefault();
        e.stopPropagation();
        
        // Get the cat number from the parent section
        const trailBoxSection = e.target.closest('.trail-box-section');
        const catNumberAttr = trailBoxSection ? trailBoxSection.getAttribute('data-cat') : null;
        const catNumber = catNumberAttr ? parseInt(catNumberAttr) : 1;
        
        this.openTrailBoxSystem(catNumber);
      }
      // Bundle system close button
      else if (e.target.closest('.bundle-system-close')) {
        this.closeBundleSystem();
      }
      // Quiz checkout button
      else if (e.target.closest('.quiz-checkout-btn')) {
        this.handleCheckout(e);
      }
      // Plan tabs (trial/monthly)
      else if (e.target.classList.contains('tab') && e.target.dataset.tab) {
        this.handlePlanTabChange(e.target);
      }
    });

    // Handle breed unknown checkbox (with error handling)
    try {
      this.setupBreedUnknownHandler();
    } catch (error) {
    }
    
  }

  setupBreedUnknownHandlerForCat(catNumber) {
    // Set up handler for a specific cat
    const step = document.querySelector(`.quiz-step[data-step="4"][data-cat="${catNumber}"]`);
    if (step) {
      this.attachBreedHandlerToStep(step);
    }
  }

  setupBreedUnknownHandler() {
    // Get all breed steps (for all cats)
    const breedSteps = document.querySelectorAll('.quiz-step[data-step="4"]');
    
    breedSteps.forEach(step => {
      this.attachBreedHandlerToStep(step);
    });
  }

  attachBreedHandlerToStep(step) {
      const catIndex = parseInt(step.getAttribute('data-cat')) || 1;
      console.log('Attaching breed handler for cat:', catIndex);
      
      // For multi-cat support, the name might be cat2_breed, cat3_breed, etc.
      // First try to find by data-original-name, then by name
      let breedSelect = step.querySelector('select[data-original-name="breed"]');
      if (!breedSelect) {
        breedSelect = step.querySelector('select[name="breed"]');
      }
      if (!breedSelect) {
        // For multi-cat, the name might be cat2_breed, cat3_breed, etc.
        breedSelect = step.querySelector(`select[name="cat${catIndex}_breed"]`);
      }
      
      const breedCheckbox = step.querySelector('.breed-unknown-checkbox input[type="checkbox"]');
      

      
      if (!breedSelect || !breedCheckbox) return;
      
      // Check if handlers are already attached to avoid duplicates
      if (breedCheckbox.hasAttribute('data-breed-handler-attached')) {
        return;
      }
      
      // Mark that handlers are attached
      breedCheckbox.setAttribute('data-breed-handler-attached', 'true');
      
      // Flag to prevent infinite loops
      let isUpdating = false;
      
      // Handle checkbox change
      breedCheckbox.addEventListener('change', (e) => {
        if (isUpdating) return;
        isUpdating = true;
        
        console.log(`Breed checkbox changed for cat ${catIndex}:`, e.target.checked);
        console.log('Breed select element:', breedSelect);
        console.log('Available options:', Array.from(breedSelect.options).map(o => o.value));
        
        if (e.target.checked) {
          // Try different quote variations in case of encoding issues
          const optionValue = "I don't know";
          
          // Find the exact matching option
          const matchingOption = Array.from(breedSelect.options).find(opt => 
            opt.value === optionValue || 
            opt.value === "I don't know" || 
            opt.value === "I don't know" ||
            opt.value === "I don\u2019t know"
          );
          
          if (matchingOption) {
            breedSelect.value = matchingOption.value;
            console.log('Found and set matching option:', matchingOption.value);
          } else {
            // Fallback - set directly
            breedSelect.value = "I don't know";
            console.log('Direct set to:', breedSelect.value);
          }
          
          // Update the appropriate cat's data using QuizState
          window.QuizState.updateCatData(catIndex, 'breed', breedSelect.value);
        } else {
          breedSelect.value = "";
          // Update the appropriate cat's data using QuizState
          window.QuizState.updateCatData(catIndex, 'breed', "");
        }
        
        // Get updated data
        this.submissionData = window.QuizState.get('submissionData');
        this.updateStepDisplay();
        
        // Trigger change event on select to handle validation
        setTimeout(() => {
          isUpdating = false;
          breedSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }, 10);
      });
      
      // Handle select change
      breedSelect.addEventListener('change', (e) => {
        if (isUpdating) return;
        isUpdating = true;
        
        console.log(`Breed select changed for cat ${catIndex}:`, e.target.value);
        
        // Check for different quote variations
        const value = e.target.value;
        if (value === "I don't know" || 
            value === "I don't know" || 
            value === "I don't know" ||
            value === "I don\u2019t know") {
          breedCheckbox.checked = true;
        } else {
          breedCheckbox.checked = false;
        }
        
        // Update the appropriate cat's data using QuizState
        window.QuizState.updateCatData(catIndex, 'breed', e.target.value);
        
        // Get updated data
        this.submissionData = window.QuizState.get('submissionData');
        
        setTimeout(() => {
          isUpdating = false;
        }, 10);
      });
      
      // Set initial state based on saved data
      const catData = window.QuizState.getCat(catIndex);
      const savedBreed = catData ? catData.breed : '';
      
      console.log(`Initial breed for cat ${catIndex}:`, savedBreed);
      
      // Check for different quote variations in saved data
      if (savedBreed === "I don't know" || 
          savedBreed === "I don't know" || 
          savedBreed === "I don't know" ||
          savedBreed === "I don\u2019t know") {
        breedCheckbox.checked = true;
        // Find and set the matching option
        const matchingOption = Array.from(breedSelect.options).find(opt => 
          opt.value === savedBreed || 
          opt.value === "I don't know" || 
          opt.value === "I don't know" ||
          opt.value === "I don\u2019t know"
        );
        if (matchingOption) {
          breedSelect.value = matchingOption.value;
        } else {
          breedSelect.value = "I don't know";
        }
      } else if (savedBreed) {
        breedCheckbox.checked = false;
        breedSelect.value = savedBreed;
      }
  }

  handleInput(e) {
    const input = e.target;
    
    // Update input visual state for consistent active styling
    this.updateInputState(input);
    
    // Ensure we have the latest data from QuizState
    this.submissionData = window.QuizState.get('submissionData') || this.submissionData;
    
    // Check if this is a multi-cat input
    const originalName = input.getAttribute('data-original-name');
    const catNumber = parseInt(input.getAttribute('data-cat'));
    
    if (originalName && catNumber) {
      // Multi-cat handling
      this.handleMultiCatInput(input, originalName, catNumber);
    } else {
      // Handle cat 1 fields by delegating to handleMultiCatInput
      const { name } = input;
      
      // Check if this is a cat 1 field (based on our known cat fields)
      const catFields = ['fuzzballer_name', 'breed',
                        'age_range',
                        'wet_dry_preference', 'cans_per_day',
                        'health_concerns_exists', 'health_concerns',
                        'lifestyle', 'disliked_flavors'];
      
      if (catFields.includes(name)) {
        // This is a cat 1 field - use the multi-cat handler
        this.handleMultiCatInput(input, name, 1);
      } else {
        // Global fields (customer_name, customer_email)
        const { value, type, checked } = input;
        
        if (type === 'checkbox') {
          this.handleCheckboxInput(name, value, checked);
        } else {
          this.submissionData[name] = value;
        }

        // Handle step 6 validation
        if (name === 'customer_name' || name === 'customer_email') {
          this.validateStep6();
        }

        // Real-time save to localStorage
        window.QuizState.save('submissionData', this.submissionData);
      }
    }
    
    // Update button states based on validation
    this.skipAutoScroll = true; // Prevent double scroll
    this.updateStepDisplay();
    this.skipAutoScroll = false;
    
    // Auto-scroll to next question when current question is complete
    const inputGroup = input.closest('.input-group');
    if (inputGroup && this.isQuestionComplete(inputGroup)) {
      // Debounce the scroll to prevent rapid triggering
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
      }
      this.scrollTimer = setTimeout(() => {
        this.scrollToNextQuestion(inputGroup);
      }, 100);
    }
    
    // Update skip button state after any input change
    this.updateSkipButtonState();
  }

  updateInputState(input) {
    // Handle text inputs, email inputs, number inputs, and select dropdowns with consistent active styling
    if (input.type === 'text' || input.type === 'number' || input.type === 'email' || input.tagName.toLowerCase() === 'select') {
      const hasValue = input.value && input.value.trim() !== '';
      const inputGroup = input.closest('.input-group');
      
      if (hasValue) {
        // All inputs get the filled class for border styling
        input.classList.add('input-filled');
        
        // Only text inputs get the tick mark - applied to input-group container
        if (input.type === 'text' && inputGroup) {
          inputGroup.classList.add('input-group-filled-with-tick');
        }
      } else {
        // Remove classes when empty
        input.classList.remove('input-filled');
        
        // Remove tick mark class from input group
        if (inputGroup) {
          inputGroup.classList.remove('input-group-filled-with-tick');
        }
      }
    }
  }

  updateAllInputStates() {
    // Update visual state for all inputs in the quiz
    const inputs = document.querySelectorAll('.quiz-form input[type="text"], .quiz-form input[type="number"], .quiz-form input[type="email"], .quiz-form select');
    inputs.forEach(input => {
      this.updateInputState(input);
    });
  }

  updateCatNameDisplays(name) {
    const displayName = window.QuizUtils.Format.catName(name);
    window.QuizUtils.DOM.updateMultiple('.cat-name-display', displayName);
  }

  handleCheckboxInput(name, value, checked) {
    if (name === 'disliked_flavors') {
      if (checked) {
        if (!this.submissionData.disliked_flavors.includes(value)) {
          this.submissionData.disliked_flavors.push(value);
        }
      } else {
        this.submissionData.disliked_flavors = this.submissionData.disliked_flavors.filter(
          item => item !== value
        );
      }
    }
  }

  setupConditionalLogic() {
    // Initialize the conditional logic on page load
    // Handle food preference changes to show/hide dislikes section
    this.initializeFoodPreferenceLogic();
  }
  
  initializeFoodPreferenceLogic() {
    // Branching engine handles step skipping and per-cat visibility.
    // No per-step visibility toggling needed — dislikes on separate step 5.
  }

  isCatVisibleOnStep(catNumber, step) {
    const catData = this.submissionData[`cat${catNumber}`];
    if (!catData) return false;
    const foodType = catData.wet_dry_preference;
    switch (step) {
      case 3: return foodType === 'Wet' || foodType === 'Both';
      case 5: return foodType === 'Wet' || foodType === 'Both';
      default: return true;
    }
  }

  shouldSkipStep(step) {
    if (step !== 3 && step !== 5) return false;
    const catCount = this.submissionData.catCount || 1;
    for (let i = 1; i <= catCount; i++) {
      if (this.isCatVisibleOnStep(i, step)) return false;
    }
    return true;
  }

  handleFoodPreferenceChange(value, catNumber) {
    const catData = this.submissionData[`cat${catNumber}`];
    if (catData) {
      // Always clear age on food-pref change — age is conditional on step 4 (Dry/Both reveal).
      // Mirrors the cans_per_day / lifestyle clearing pattern so toggling Wet↔Dry/Both never
      // leaves stale age state behind.
      window.QuizState.updateCatData(catNumber, 'age_range', '');
      const ageStep4 = document.querySelector(`.quiz-step[data-step="4"][data-cat="${catNumber}"]`);
      if (ageStep4) {
        const ageSelect = ageStep4.querySelector('select[name="age_range"], select[name$="_age_range"]');
        if (ageSelect) ageSelect.value = '';
      }

      if (value === 'Dry') {
        window.QuizState.updateCatData(catNumber, 'cans_per_day', '');
        window.QuizState.updateCatData(catNumber, 'disliked_flavors', []);
        // Clear DOM radio/checkbox state for skipped fields
        const catStep3 = document.querySelector(`.quiz-step[data-step="3"][data-cat="${catNumber}"]`);
        if (catStep3) catStep3.querySelectorAll('input[type="radio"]').forEach(r => r.checked = false);
        const catStep5 = document.querySelector(`.quiz-step[data-step="5"][data-cat="${catNumber}"]`);
        if (catStep5) catStep5.querySelectorAll('input[type="checkbox"]').forEach(c => c.checked = false);
      } else {
        // Clear quantity so pre-select logic re-fires on slide 3
        window.QuizState.updateCatData(catNumber, 'cans_per_day', '');
        const catStep3 = document.querySelector(`.quiz-step[data-step="3"][data-cat="${catNumber}"]`);
        if (catStep3) catStep3.querySelectorAll('input[name="cans_per_day"]').forEach(r => r.checked = false);

        window.QuizState.updateCatData(catNumber, 'lifestyle', '');
        // Clear DOM radio state for lifestyle
        const catStep4 = document.querySelector(`.quiz-step[data-step="4"][data-cat="${catNumber}"]`);
        if (catStep4) {
          const lifestyleSection = catStep4.querySelector('.lifestyle-section');
          if (lifestyleSection) lifestyleSection.querySelectorAll('input[type="radio"]').forEach(r => r.checked = false);
        }
      }
    }
    this.submissionData = window.QuizState.get('submissionData') || this.submissionData;

    // Re-sync slide 3 vet message context since food preference changed
    const catStep3 = document.querySelector(`.quiz-step[data-step="3"][data-cat="${catNumber}"]`);
    if (catStep3) {
      const allVetMessages = catStep3.querySelectorAll('.vet-message[data-food-context]');
      allVetMessages.forEach(vm => { vm.style.display = 'none'; });
      if (value === 'Wet' || value === 'Both') {
        const matched = catStep3.querySelector(`.vet-message[data-food-context="${value}"]`);
        if (matched) matched.style.display = '';
      }

      // Show/hide "both" notes on slide 3 quantity options
      const bothNotes = catStep3.querySelectorAll('.radio-option__both-note');
      bothNotes.forEach(note => {
        note.style.display = value === 'Both' ? '' : 'none';
      });

      // Swap label + subtitle to "both"-path variants when food pref = Both,
      // but only if the corresponding `_both` field has content (the both span
      // is only rendered by liquid when != blank). Empty-state: leave wet-only
      // text untouched so the both path renders byte-identically to today.
      this.toggleQuantityBothLabels(catStep3, value === 'Both');
    }
  }

  toggleQuantityBothLabels(stepEl, useBoth) {
    const radioOptions = stepEl.querySelectorAll('.radio-option');
    radioOptions.forEach(option => {
      const wetTitle = option.querySelector('.radio-option__title:not(.radio-option__title--both)');
      const bothTitle = option.querySelector('.radio-option__title--both');
      const wetSubtitle = option.querySelector('.radio-option__subtitle:not(.radio-option__subtitle--both)');
      const bothSubtitle = option.querySelector('.radio-option__subtitle--both');

      if (useBoth) {
        // Only swap when the both variant exists; otherwise leave wet text alone.
        if (bothTitle) {
          if (wetTitle) wetTitle.style.display = 'none';
          bothTitle.style.display = '';
        }
        if (bothSubtitle) {
          if (wetSubtitle) wetSubtitle.style.display = 'none';
          bothSubtitle.style.display = '';
        }
      } else {
        if (wetTitle) wetTitle.style.display = '';
        if (bothTitle) bothTitle.style.display = 'none';
        if (wetSubtitle) wetSubtitle.style.display = '';
        if (bothSubtitle) bothSubtitle.style.display = 'none';
      }
    });
  }

  initializeCatGreyAnimations() {
    // Initialize cat grey animations for steps 2-5
    if (typeof lottie === 'undefined') {
      return;
    }
    
    const animationContainers = document.querySelectorAll('.quiz-cat-grey-animation');
    const animationPath = window.catGreyAnimationUrl || '/assets/Fuzzball-Cat-Grey.json';
    
    animationContainers.forEach(container => {
      if (!container.hasAttribute('data-lottie-initialized')) {
        try {
          const animation = lottie.loadAnimation({
            container: container,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            path: animationPath
          });
          
          // Mark as initialized to prevent duplicate animations
          container.setAttribute('data-lottie-initialized', 'true');
          
          // Store animation reference if needed
          container.lottieAnimation = animation;
        } catch (error) {
        }
      }
    });
  }

  
  handleHealthConcernsChange(value, catNumber = 1) {
    
    // Find the step 4 section for this cat
    let catSection;
    if (catNumber === 1) {
      catSection = document.querySelector('.quiz-step[data-step="4"][data-cat="1"], .quiz-step[data-step="4"]:not([data-cat])');
    } else {
      catSection = document.querySelector(`.quiz-step[data-step="4"][data-cat="${catNumber}"]`);
    }


    if (!catSection) {
      return;
    }

    // Find elements within the specific cat section
    const healthDropdown = catSection.querySelector('.health-concerns-dropdown');
    const healthTipMessage = catSection.querySelector('.health-tip-message');
    const healthConcernsSelect = catSection.querySelector('select[name="health_concerns"], select[data-original-name="health_concerns"]');
    
    
    if (!healthDropdown) {
      return;
    }
    
    // Get the cat data
    const catData = catNumber === 1 ? this.submissionData : this.submissionData[`cat${catNumber}`];
    
    if (value === 'Yes') {
      // Show dropdown
      window.QuizUtils.DOM.show(healthDropdown);
      if (healthConcernsSelect) healthConcernsSelect.required = true;
      
      // If switching from No (value was empty), reset to empty so user must select
      if (!catData?.health_concerns) {
        if (healthConcernsSelect) healthConcernsSelect.value = '';
      }
    } else if (value === 'No') {
      // Hide dropdown and tip message
      window.QuizUtils.DOM.hide(healthDropdown);
      if (healthTipMessage) window.QuizUtils.DOM.hide(healthTipMessage);
      if (healthConcernsSelect) {
        healthConcernsSelect.required = false;
        healthConcernsSelect.value = '';
      }
    }
  }
  
  handleHealthConcernSelection(value, catNumber = 1) {
    
    // Find the step 4 section for this cat
    let catSection;
    if (catNumber === 1) {
      catSection = document.querySelector('.quiz-step[data-step="4"][data-cat="1"], .quiz-step[data-step="4"]:not([data-cat])');
    } else {
      catSection = document.querySelector(`.quiz-step[data-step="4"][data-cat="${catNumber}"]`);
    }
    
    if (!catSection) {
      return;
    }
    
    // Find elements within the specific cat section
    const healthTipMessage = catSection.querySelector('.health-tip-message');
    const healthTipText = catSection.querySelector('.health-tip-text');
    
    if (!healthTipMessage || !healthTipText) {
      return;
    }
    
    const healthMessages = window.QuizConstants.HEALTH_MESSAGES;
    
    if (value && healthMessages[value]) {
      healthTipText.textContent = healthMessages[value];
      window.QuizUtils.DOM.show(healthTipMessage);
      
      // Add special styling for chronic kidney disease
      if (value === 'Chronic kidney disease') {
        healthTipMessage.classList.add('health-tip-warning');
      } else {
        healthTipMessage.classList.remove('health-tip-warning');
      }
    } else {
      window.QuizUtils.DOM.hide(healthTipMessage);
      healthTipMessage.classList.remove('health-tip-warning');
    }
  }

  validateStep6() {
    const nameInput = document.querySelector('input[name="customer_name"]');
    const emailInput = document.querySelector('input[name="customer_email"]');
    const step6Button = document.querySelector('.step-6-button');
    const nextBtn = document.querySelector('.quiz-next-btn');
    const navForwardBtn = document.querySelector('.quiz-nav-forward');

    if (!nameInput || !emailInput) return;

    const nameValid = nameInput.value.trim().length > 0;
    const emailValid = emailInput.value.trim().length > 0 && emailInput.validity.valid;

    const isValid = nameValid && emailValid;

    // The step-6 submit button mirrors the bottom next-btn / forward arrow:
    // gated on validity only. After a prior Klaviyo submit it stays live so a
    // user returning to this step can advance via the button they naturally
    // reach for; the re-submit guard lives in handleStep6Submission(), which
    // short-circuits to step 7 without a second Klaviyo call.
    if (step6Button) step6Button.disabled = !isValid;
    if (nextBtn && this.currentStep === 6) nextBtn.disabled = !isValid;
    // Forward arrow mirrors the bottom next-btn state.
    if (navForwardBtn && this.currentStep === 6) navForwardBtn.disabled = !isValid;

  }

  async handleStep6Submission() {
    // Validate step 6 first
    if (!window.QuizValidators.validateStep(6, this.submissionData)) {
      return;
    }

    // Short-circuit if Klaviyo submit already succeeded this session (D3.3).
    // User can still navigate forward to step 7 without re-submitting.
    if (window.QuizState.get('quiz.klaviyoSubmitted')) {
      this.goToStep7();
      return;
    }

    // Define startTime at function scope so it's available in finally block
    const startTime = Date.now();

    try {
      // Show loading state on button
      const step6Button = document.querySelector('.step-6-button');
      const nextBtn = document.querySelector('.quiz-next-btn');
      const activeButton = step6Button || nextBtn;

      if (activeButton) {
        activeButton.disabled = true;
        activeButton.classList.add('loading');
      }

      // Returning-customer check (Quiz step 6 — 180-day repeat-trial check).
      // Runs after the validate/already-submitted guards and BEFORE Klaviyo.
      // If the email belongs to a prior Fuzzball trial customer, show the modal
      // and return early: do NOT submit to Klaviyo and do NOT advance to step 7.
      // The endpoint URL doubles as the on/off toggle and the check fails open,
      // so any error / blank config leaves the existing flow unchanged. The
      // shared finally block still enforces the ~1.5s minimum loading state.
      const priorTrial = await this.checkPriorTrial(this.submissionData);
      if (priorTrial) {
        this.showRepeatTrialModal();
        return;
      }

      // Submit to Klaviyo using API
      if (window.KlaviyoAPISubmitter) {
        const klaviyoSubmitter = new window.KlaviyoAPISubmitter();
        const result = await klaviyoSubmitter.submitQuizData(this.submissionData);

        if (result.success) {
          // Mark Klaviyo submit so back-then-forward can't re-submit (D3.3)
          window.QuizState.set('quiz.klaviyoSubmitted', true);
          window.QuizState.save('klaviyo_submitted', 'true');
        } else {
          // Continue anyway - don't block quiz progression
        }
      } else {
      }

      // Small delay to ensure Klaviyo processes
      await new Promise(resolve => setTimeout(resolve, 500));

      // Proceed to step 7
      this.goToStep7();

    } catch (error) {
      // Continue to step 7 even if submission fails
      this.goToStep7();
    } finally {
      // Ensure minimum loading time of 1.5 seconds for visibility
      const elapsedTime = Date.now() - startTime;
      const minimumTime = 1500; // 1.5 seconds
      
      if (elapsedTime < minimumTime) {
        await new Promise(resolve => setTimeout(resolve, minimumTime - elapsedTime));
      }
      
      // Remove loading state
      const step6Button = document.querySelector('.step-6-button');
      const nextBtn = document.querySelector('.quiz-next-btn');
      const activeButton = step6Button || nextBtn;
      
      if (activeButton) {
        activeButton.disabled = false;
        activeButton.classList.remove('loading');
      }
    }
  }

  // POST the email to the prior-order-check endpoint and report whether this is
  // a returning Fuzzball trial customer. Fails open (returns false) on a blank
  // endpoint (feature off), a missing email, a non-OK response, or any error —
  // so the quiz always proceeds rather than blocking a genuine signup.
  async checkPriorTrial(submissionData) {
    try {
      const quizContainer = document.querySelector('.fuzzball-quiz-container');
      const endpoint = (quizContainer?.dataset.repeatCheckEndpoint || '').trim();
      if (!endpoint) return false; // no endpoint configured = check disabled

      const email = (submissionData?.customer_email || '').trim();
      if (!email) return false;

      const body = { email };
      const windowDays = parseInt(quizContainer?.dataset.repeatCheckWindowDays || '', 10);
      if (!isNaN(windowDays) && windowDays > 0) body.windowDays = windowDays;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) return false; // fail open

      const data = await response.json();
      return !!(data && data.priorTrial === true);
    } catch (error) {
      return false; // fail open on network/parse error
    }
  }

  // Show the returning-customer modal (single CTA). The CTA redirects to the
  // configured account portal; dismiss (Escape/backdrop) just closes and leaves
  // the user on step 6. Copy + portal URL come from the customizer via data-*.
  showRepeatTrialModal() {
    const quizContainer = document.querySelector('.fuzzball-quiz-container');
    const ds = quizContainer ? quizContainer.dataset : {};

    const title = (ds.repeatModalTitle || '').trim() || 'Welcome back! 🐾';
    const body = (ds.repeatModalBody || '').trim() ||
      "Looks like you've already started a Fuzzball trial. You can manage your plan, add a cat, or update your subscription in your account — no need to start over.";
    const ctaLabel = (ds.repeatModalCtaLabel || '').trim() || 'Manage my subscription';

    // The portal URL comes from a Shopify `url` setting, which is a FREE-TEXT
    // field (not a closed picker) — so a customizer value could be a dangerous
    // scheme (javascript:/data:/vbscript:) and this string is fed straight into
    // location.href. Allow only http(s) absolute URLs and root-relative paths;
    // anything else falls back to the safe default.
    const DEFAULT_PORTAL_URL = 'https://fuzzball.club/tools/recurring/login';
    const rawPortalUrl = (ds.repeatPortalUrl || '').trim();
    let portalUrl = DEFAULT_PORTAL_URL;
    if (rawPortalUrl) {
      if (rawPortalUrl.charAt(0) === '/' && rawPortalUrl.charAt(1) !== '/') {
        portalUrl = rawPortalUrl; // root-relative path (not protocol-relative)
      } else {
        try {
          const parsed = new URL(rawPortalUrl, window.location.origin);
          if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
            portalUrl = parsed.href;
          }
        } catch (_) { /* malformed → keep default */ }
      }
    }

    if (typeof window.fuzzballCtaAlert !== 'function') {
      // Defensive fallback — without the modal helper, fail open to the portal.
      window.location.href = portalUrl;
      return;
    }

    window.fuzzballCtaAlert(body, { title, buttonText: ctaLabel }).then(function (result) {
      // CTA click resolves true → redirect; dismiss resolves undefined → stay.
      if (result === true) {
        window.location.href = portalUrl;
      }
    });
  }

  goToStep7() {
    if (window.QuizValidators.validateStep(6, this.submissionData)) {
      // Calculate recommendations for all cats
      const recommendations = this.calculateAllCatsRecommendations();
      
      // Store recommendations
      window.QuizState.updateRecommendations(recommendations);
      
      // Mark quiz as effectively completed for bundle system initialization
      window.QuizState.markQuizComplete();
      
      // Move to step 7
      this.currentStep = 7;
      this.updateStepDisplay();
      this.updateProgressDisplay();
      
      // Update step 7 content with recommendations
      this.updateStep7Content(recommendations);
      
      // Initialize bundle system with recommendations
      this.initializeBundleSystemWithRecommendations(recommendations);
      
      // Update meal plan counts after bundle initialization (with a delay to ensure bundle is ready)
      if (recommendations.cats && Object.keys(recommendations.cats).length > 1) {
        setTimeout(() => {
          this.updateStep7MealPlanCounts();
        }, 500);
      }
    }
  }

  updateStep7Content(recommendations) {
    // Store recommendations for later use
    this.lastCalculatedRecommendations = recommendations;
    
    // Check if we're in edit mode for a specific cat
    const currentEditMode = window.QuizState.get('editMode');
    const isEditingSpecificCat = currentEditMode?.active && currentEditMode?.catNumber;
    
    // If editing a specific cat, treat as single cat for rendering
    const numberOfCats = isEditingSpecificCat ? 1 : (this.submissionData.catCount || 1);
    
    // Update trial price in both single and multi-cat scenarios
    const trialTabContent = document.querySelector('.tab-content[data-tab-content="trial"]');
    if (trialTabContent) {
      let totalTrialPrice = window.QuizConstants.FOOD.TRIAL_BOX_PRICE * numberOfCats;
      
      // Add extras total to the trial tab price
      if (window.QuizExtrasManager) {
        const extrasTotal = window.QuizExtrasManager.getTotalPrice();
        totalTrialPrice += extrasTotal;
      }
      
      const trialPlanPrice = document.querySelector('.tab[data-tab="trial"] .plan-price');
      if (trialPlanPrice) {
        trialPlanPrice.textContent = `£${totalTrialPrice.toFixed(2)}`;
      }
    }
    
    // Update checkout bar trial total
    const trialCheckoutTotal = document.querySelector('.trial-checkout-total');
    if (trialCheckoutTotal) {
      let totalTrialPrice = window.QuizConstants.FOOD.TRIAL_BOX_PRICE * numberOfCats;
      
      // Add extras total from all cats
      if (window.QuizExtrasManager) {
        const extrasTotal = window.QuizExtrasManager.getTotalPrice(); // Gets total across all cats
        totalTrialPrice += extrasTotal;
      }
      
      trialCheckoutTotal.textContent = `£${totalTrialPrice.toFixed(2)}`;
    }
    
    // Update checkout bar for the active tab (trial is default)
    const activeTab = document.querySelector('.plan-tabs .tab.active');
    if (activeTab) {
      this.updateCheckoutBarForTab(activeTab.dataset.tab);
    } else {
      // Default to trial if no active tab found
      this.updateCheckoutBarForTab('trial');
    }
    
    if (numberOfCats === 1) {
      // Single cat - update as before
      const catRec = recommendations.cats ? recommendations.cats.cat1 : recommendations;
      
      // Update cat name
      window.QuizUtils.DOM.updateMultiple('.cat-name-display', catRec.catName || 'your cat');

      // Prefer live bundle selections; fall back to lower-bound estimate
      const bundlePrice = this.getLiveOrEstimatedBundlePrice(catRec);
      
      // Update all bundle price elements
      window.QuizUtils.DOM.updateMultiple('.bundle-total-price', `£${bundlePrice.toFixed(2)}`);
      window.QuizUtils.DOM.updateMultiple('.bundle-system-price', `£${bundlePrice.toFixed(2)}`);
    } else {
      // Multiple cats - need to update step 7 structure
      this.renderMultiCatResults(recommendations);
    }
    
    // Update extras display
    if (window.QuizExtrasManager) {
      window.QuizExtrasManager.updateUI();
    }
    
    // Check if bundle system is already initialized and update price from actual selections
    setTimeout(() => {
      const bundleSystem = document.querySelector('quiz-bundle');
      if (bundleSystem && bundleSystem.selections && bundleSystem.selections.length > 0) {
        const actualPrice = bundleSystem.selections.reduce((sum, item) => sum + (item.price * item.quantity), 0);

        window.QuizUtils.DOM.updateMultiple('.bundle-total-price', `£${actualPrice.toFixed(2)}`);
        window.QuizUtils.DOM.updateMultiple('.bundle-system-price', `£${actualPrice.toFixed(2)}`);

        this.updateFreeShippingBar();
      }
    }, window.QuizConstants.TIMING.PRICE_UPDATE_DELAY);
    
    // Update total items count - handle both single and multi-cat structures
    let totalItems, bagsNeeded, packsNeeded, cansNeeded;
    
    if (recommendations.totals) {
      // Multi-cat structure
      totalItems = recommendations.totals.bagsNeeded + recommendations.totals.packsNeeded;
      bagsNeeded = recommendations.totals.bagsNeeded;
      packsNeeded = recommendations.totals.packsNeeded;
      cansNeeded = recommendations.totals.cansNeeded;
    } else {
      // Single cat structure
      totalItems = recommendations.bagsNeeded + recommendations.packsNeeded;
      bagsNeeded = recommendations.bagsNeeded;
      packsNeeded = recommendations.packsNeeded;
      cansNeeded = recommendations.cansNeeded;
    }
    
    window.QuizUtils.DOM.updateMultiple('.total-items-count', totalItems);
    
    // Update delivery total text
    const deliveryText = window.QuizUtils.Format.deliveryText(bagsNeeded, cansNeeded);
    window.QuizUtils.DOM.updateMultiple('.total-items-text', deliveryText);
    
    // Update plan summary section
    this.updatePlanSummary(recommendations);

    // Update trail box count in delivery-info section
    const deliveryTrailBoxCount = document.querySelector('.delivery-info .trail-box-items-count');
    if (deliveryTrailBoxCount) {
      if (numberOfCats === 1) {
        // Single cat - get actual trail box selections or default
        const trailBoxElement = document.querySelector('trail-box');
        if (trailBoxElement && trailBoxElement.selections && trailBoxElement.selections.cat1) {
          const totalItems = trailBoxElement.selections.cat1.reduce((sum, item) => sum + item.quantity, 0);
          deliveryTrailBoxCount.textContent = totalItems;
        } else {
          // Use default based on diet preference
          const catRec = recommendations.cats ? recommendations.cats.cat1 : recommendations;
          const dietPref = catRec.diet || 'Mix of both';
          const defaultCount = this.getTrailBoxItemsCount(dietPref);
          deliveryTrailBoxCount.textContent = defaultCount;
        }
      } else {
        // Multi-cat - sum all trail box items
        let totalTrailBoxItems = 0;
        Object.values(recommendations.cats).forEach(catRec => {
          totalTrailBoxItems += this.getTrailBoxItemsCount(catRec.diet);
        });
        deliveryTrailBoxCount.textContent = totalTrailBoxItems;
      }
    }
    
    
    // Update extras display for step 7
    if (window.QuizExtrasManager) {
      window.QuizExtrasManager.updateUI();
    }
    
    // Update trail box items count for single cat mode
    if (numberOfCats === 1) {
      const trailBoxItemsCount = document.querySelector('.trail-box-items-info .trail-box-items-count');
      if (trailBoxItemsCount) {
        // Get actual trail box selections count
        const trailBoxElement = document.querySelector('trail-box');
        if (trailBoxElement && trailBoxElement.selections && trailBoxElement.selections.cat1) {
          const totalItems = trailBoxElement.selections.cat1.reduce((sum, item) => sum + item.quantity, 0);
          trailBoxItemsCount.textContent = totalItems;
        } else {
          // If no selections yet, use default based on diet preference
          const catRec = recommendations.cats ? recommendations.cats.cat1 : recommendations;
          const dietPref = catRec.diet || 'Mix of both';
          const defaultCount = this.getTrailBoxItemsCount(dietPref);
          trailBoxItemsCount.textContent = defaultCount;
        }
      }
    }
    
    // Initialize trail box with auto-population (but not in edit mode or multi-cat)
    const editMode = window.QuizState.get('editMode');
    const numCats = this.submissionData.catCount || 1;
    const trailBoxBuilder = document.querySelector('trail-box');
    
    // Function to ensure trail box counts are updated
    const ensureTrailBoxCountUpdate = () => {
      const trailBoxElement = document.querySelector('trail-box');
      if (trailBoxElement && trailBoxElement.selections && trailBoxElement.selections.cat1) {
        const totalItems = trailBoxElement.selections.cat1.reduce((sum, item) => sum + item.quantity, 0);
        
        // Update all trail box count displays
        const allTrailBoxCounts = document.querySelectorAll('.trail-box-items-count');
        allTrailBoxCounts.forEach(element => {
          if (!element.closest('[data-cat]')) { // Skip multi-cat elements
            element.textContent = totalItems;
          }
        });
      } else {
        // If trail box isn't ready, try again after a short delay
        setTimeout(ensureTrailBoxCountUpdate, 200);
      }
    };
    
    if (trailBoxBuilder && trailBoxBuilder.autoPopulateFromQuiz && !editMode?.active && numCats === 1) {
      trailBoxBuilder.autoPopulateFromQuiz();
      
      // Update trail box count after auto-population with retry logic
      setTimeout(ensureTrailBoxCountUpdate, 100);
    } else if (editMode?.active) {
      // Still update count in edit mode to show restored selections
      if (numCats === 1) {
        setTimeout(ensureTrailBoxCountUpdate, 100);
      }
    } else if (numCats > 1) {
      this.ensureTrailBoxSelectionsForAllCats(recommendations, trailBoxBuilder);
    } else if (numCats === 1) {
      // Single cat mode - ensure count is updated even if trail box isn't ready
      setTimeout(ensureTrailBoxCountUpdate, 100);
    }
  }

  // Reads cart.attributes._first_delivery_date — set by the first-delivery
  // modal on Confirm. Cached + de-duped so concurrent updatePlanSummary
  // calls (controller + extras manager) don't race or flicker.
  async getFirstDeliveryDateFromCart() {
    if (!window.FUZZBALL_FIRST_DELIVERY_ENABLED) return null;
    if (this._cachedFirstDeliveryDate !== undefined) return this._cachedFirstDeliveryDate;
    if (this._inFlightFddFetch) return this._inFlightFddFetch;

    this._inFlightFddFetch = (async () => {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = await res.json();
        const value = (cart && cart.attributes && cart.attributes._first_delivery_date) || null;
        this._cachedFirstDeliveryDate = value;
        return value;
      } catch {
        this._cachedFirstDeliveryDate = null;
        return null;
      } finally {
        this._inFlightFddFetch = null;
      }
    })();
    return this._inFlightFddFetch;
  }

  async updatePlanSummary(recommendations) {
    const container = document.querySelector('.your-plan-summary');
    if (!container) return;

    const numberOfCats = this.submissionData.catCount || 1;
    const trialBoxPrice = window.QuizConstants.FOOD.TRIAL_BOX_PRICE;
    const delayDays = window.QuizConstants.FOOD.SUBSCRIPTION_DELAY_DAYS;
    const daysPerMonth = window.QuizConstants.FOOD.DAYS_PER_MONTH;

    // --- Today ---
    let totalTrialPrice = trialBoxPrice * numberOfCats;
    let extrasTotal = 0;
    if (window.QuizExtrasManager) {
      extrasTotal = window.QuizExtrasManager.getTotalPrice();
      totalTrialPrice += extrasTotal;
    }
    const todayEl = container.querySelector('.your-plan-summary__today-value');
    if (todayEl) {
      const trialLabel = extrasTotal > 0 ? 'Trial Box and Extras' : 'Trial Box';
      todayEl.textContent = `£${totalTrialPrice.toFixed(2)} ${trialLabel}`;
    }

    // --- Next Shipment ---
    // Prefer the customer's chosen date from cart attribute (set by the
    // first-delivery modal). Fall back to today + SUBSCRIPTION_DELAY_DAYS.
    const nextShipmentEl = container.querySelector('.your-plan-summary__next-shipment-value');
    if (nextShipmentEl) {
      const chosen = await this.getFirstDeliveryDateFromCart();
      let shipDate;
      if (chosen) {
        shipDate = new Date(chosen + 'T00:00:00');
        if (isNaN(shipDate)) {
          shipDate = new Date();
          shipDate.setDate(shipDate.getDate() + delayDays);
        }
      } else {
        shipDate = new Date();
        shipDate.setDate(shipDate.getDate() + delayDays);
      }
      const day = shipDate.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const suffix = (day === 1 || day === 21 || day === 31) ? 'st' : (day === 2 || day === 22) ? 'nd' : (day === 3 || day === 23) ? 'rd' : 'th';
      nextShipmentEl.textContent = `${monthNames[shipDate.getMonth()]} ${day}${suffix} - Full plan`;
    }

    // --- Then (cans + bags every 28 days) ---
    let totalCans = 0;
    let totalBags = 0;
    if (recommendations.cats) {
      Object.values(recommendations.cats).forEach(catRec => {
        totalCans += (catRec.packsNeeded || 0) * window.QuizConstants.FOOD.CANS_PER_PACK;
        totalBags += catRec.bagsNeeded || 0;
      });
    } else {
      totalCans = (recommendations.packsNeeded || 0) * window.QuizConstants.FOOD.CANS_PER_PACK;
      totalBags = recommendations.bagsNeeded || 0;
    }

    const thenEl = container.querySelector('.your-plan-summary__then-value');
    if (thenEl) {
      const parts = [];
      if (totalCans > 0) parts.push(`${totalCans} cans`);
      if (totalBags > 0) parts.push(`${totalBags} bag${totalBags !== 1 ? 's' : ''}`);
      thenEl.textContent = `${parts.join(' + ')} every ${daysPerMonth} days`;
    }

    // --- Price per day ---
    // Prefer live bundle selections; fall back to lower-bound estimate
    const monthlyTotal = this.getLiveOrEstimatedBundlePrice(recommendations);

    const priceEl = container.querySelector('.your-plan-summary__price-value');
    if (priceEl) {
      const pricePerDay = monthlyTotal / daysPerMonth;
      priceEl.textContent = `£${pricePerDay.toFixed(2)}/day`;
    }

    const monthlyEl = container.querySelector('.your-plan-summary__monthly-value');
    if (monthlyEl) {
      monthlyEl.textContent = `£${monthlyTotal.toFixed(2)}`;
    }

    // --- Savings ---
    // Strip any currency symbol / stray chars the merchant may have typed into
    // the "Was Price" setting (e.g. "£23") so parseFloat doesn't return NaN and
    // silently hide the whole savings row.
    const wasPrice = parseFloat(String(container.dataset.wasPrice || '').replace(/[^0-9.]/g, ''));
    const savingsRow = container.querySelector('.your-plan-summary__row--savings');
    const savingsValueEl = container.querySelector('.your-plan-summary__savings-value');

    if (savingsRow && savingsValueEl) {
      let totalSavings = 0;

      // Trial box savings: (was_price - trialBoxPrice) × number of cats
      if (!isNaN(wasPrice) && wasPrice > trialBoxPrice) {
        totalSavings += (wasPrice - trialBoxPrice) * numberOfCats;
      }

      // Extras savings: extras are 50% off, so savings = what they pay
      if (extrasTotal > 0) {
        totalSavings += extrasTotal;
      }

      if (totalSavings > 0) {
        savingsValueEl.textContent = `£${totalSavings.toFixed(2)}`;
        savingsRow.style.display = '';
      } else {
        savingsRow.style.display = 'none';
      }
    }

    // --- Free shipping progress bar (mirrors displayed totals) ---
    this.updateFreeShippingBar();
  }

  // Reads the currently-displayed monthly bundle total straight off the DOM
  // (.tab__price.bundle-total-price on the monthly tab) so the bar tracks
  // exactly what the customer sees in writing on the page — not any
  // post-discount checkout figure. Called from every existing update site
  // that mutates that number. If the bar wasn't rendered (toggle off),
  // this is a no-op.
  updateFreeShippingBar() {
    // One bar in the Monthly plan tab, one in the Edit monthly plan pop-up
    const bars = document.querySelectorAll('[data-free-shipping-bar]');
    if (!bars.length) return;

    const parsePrice = (el) => {
      if (!el) return 0;
      const txt = (el.textContent || '').replace(/[^0-9.\-]/g, '');
      const n = parseFloat(txt);
      return isNaN(n) ? 0 : n;
    };

    const monthlyEl = document.querySelector('.tab[data-tab="monthly"] .bundle-total-price');
    const monthlyTotal = parsePrice(monthlyEl);
    const total = monthlyTotal;

    bars.forEach((bar) => {
      const threshold = parseFloat(bar.dataset.freeShippingThreshold) || 0;
      const belowText = bar.dataset.freeShippingBelowText || 'Add £{amount} more to qualify for free shipping';
      const qualifiedText = bar.dataset.freeShippingQualifiedText || "You've qualified for free shipping";

      let fillPct = 0;
      if (threshold > 0) {
        fillPct = Math.max(0, Math.min(100, (total / threshold) * 100));
      } else {
        fillPct = total > 0 ? 100 : 0;
      }

      const remaining = threshold - total;
      const qualified = remaining < 0.01;

      const fillEl = bar.querySelector('[data-free-shipping-fill]');
      const labelEl = bar.querySelector('[data-free-shipping-label]');

      if (fillEl) fillEl.style.width = `${fillPct}%`;
      if (labelEl) {
        labelEl.textContent = qualified
          ? qualifiedText
          : belowText.replace('{amount}', remaining.toFixed(2));
      }
      bar.classList.toggle('quiz-step-7-free-shipping--qualified', qualified);
    });
  }

  initializeBundleSystemWithRecommendations(recommendations) {
    
    const bundleElement = document.querySelector('quiz-bundle');
    if (!bundleElement) {
      return;
    }
    
    // Check if bundle system is already initialized
    if (bundleElement.rechargeProducts && Object.keys(bundleElement.rechargeProducts).length > 0) {
      bundleElement.applyQuizRecommendations(recommendations);
    } else {
      // Wait for initialization then apply recommendations
      window.QuizUtils.Timing.waitFor(
        () => bundleElement.rechargeProducts && Object.keys(bundleElement.rechargeProducts).length > 0,
        window.QuizConstants.TIMING.DOM_CHECK_INTERVAL,
        window.QuizConstants.TIMING.RECHARGE_TIMEOUT
      ).then(() => {
        bundleElement.applyQuizRecommendations(recommendations);
      }).catch(error => {
      });
    }
  }

  openBundleSystem(catNumber = null) {
    const bundleSystem = document.querySelector('.bundle-system');
    const bundleElement = document.querySelector('quiz-bundle');
    
    if (!bundleSystem) {
      return;
    }
    
    // Store which cat we're editing if specified
    if (catNumber) {
      bundleSystem.setAttribute('data-editing-cat', catNumber);
      
      // Switch bundle element to this cat
      if (bundleElement && bundleElement.switchToCat) {
        bundleElement.switchToCat(catNumber);
      }
    } else {
      // All cats mode
      bundleSystem.removeAttribute('data-editing-cat');
      if (bundleElement) {
        bundleElement.currentCat = null;
      }
    }
    
    // Update header with cat name
    this.updateBundleSystemHeader(catNumber);
    
    // Add slide-up class to trigger animation
    bundleSystem.classList.add('bundle-system-active'); 
    
    // Show overlay
    const overlay = document.querySelector('.quiz-modal-overlay');
    if (overlay) overlay.classList.add('active');
    
    // Prevent body scrolling
    document.body.classList.add('overflow-hidden');
    
    // Ensure UI is updated after a short delay to handle edit mode
    const editMode = window.QuizState.get('editMode');
    if (editMode?.active && editMode?.catNumber) {
      setTimeout(() => {
        this.updateBundleSystemHeader(parseInt(editMode.catNumber));
      }, 100);
    }
    
    // Check if bundle system already has selections for this cat
    let hasExistingSelections = false;
    if (bundleElement && bundleElement.selections) { 
      if (catNumber) {
        // Check specific cat's selections
        hasExistingSelections = bundleElement.selections[catNumber] && bundleElement.selections[catNumber].length > 0;
      } else {
        // Check if any cat has selections
        hasExistingSelections = Object.values(bundleElement.selections).some(catSelections => 
          Array.isArray(catSelections) && catSelections.length > 0
        );
      }
    }
    
    // Only apply recommendations if there are no existing selections
    if (!hasExistingSelections) {
      let recommendations;
      
      if (catNumber) {
        // Get specific cat's recommendations
        const catData = this.submissionData[`cat${catNumber}`];
        recommendations = window.QuizCalculator.calculate(catData);
      } else {
        // Get all cats' recommendations
        recommendations = this.calculateAllCatsRecommendations();
      }
      
      // Store the fresh recommendations
      window.QuizState.updateRecommendations(recommendations);
      
      // Apply quiz recommendations to bundle system
      if (recommendations && Object.keys(recommendations).length > 0) {
        // Wait for bundle system to initialize, then apply recommendations
        setTimeout(() => {
          if (bundleElement && bundleElement.applyQuizRecommendations) {
            bundleElement.applyQuizRecommendations(recommendations);
          }
        }, window.QuizConstants.TIMING.BUNDLE_INIT_DELAY);
      }
      
    } else {
    }
  }

  updateBundleSystemHeader(catNumber = null) {
    const headerTitle = document.querySelector('.bundle-system-header h3');
    if (headerTitle) {
      if (catNumber) {
        // Specific cat
        const catData = this.submissionData[`cat${catNumber}`];
        const catName = catData?.fuzzballer_name || `Cat ${catNumber}`;
        headerTitle.textContent = `${catName}'s meal plan`;
      } else {
        // All cats or single cat
        const catCount = this.submissionData.catCount || 1;
        if (catCount === 1) {
          const catName = this.submissionData.cat1?.fuzzballer_name || this.submissionData.fuzzballer_name || 'Dave';
          headerTitle.textContent = `${catName}'s meal plan`;
        } else {
          headerTitle.textContent = `Your cats' meal plan`;
        }
      }
    }
  }

  closeBundleSystem() {
    // Get the quiz-bundle element to check selections
    const bundleElement = document.querySelector('quiz-bundle');
    
    if (bundleElement) {
      const selections = bundleElement.getSelections();
      const totalQuantity = selections.reduce((sum, item) => sum + item.quantity, 0);
      
      // Check if at least 1 item is selected
      if (totalQuantity === 0) {
        // Show error toast
        window.QuizUtils.Error.show('Please select at least 1 item for the monthly subscription');
        return; // Prevent closing
      }
      
      // Update Step 7 display for all cats BEFORE resetting currentCat
      this.updateStep7MealPlanCounts();
      
      // Reset bundle element to show all cats AFTER updating counts
      if (bundleElement) {
        bundleElement.currentCat = null;
      }
    }
    
    // If validation passes, proceed with closing
    const bundleSystem = document.querySelector('.bundle-system');
    if (bundleSystem) {
      bundleSystem.classList.remove('bundle-system-active');
    }
    
    // Hide overlay
    const overlay = document.querySelector('.quiz-modal-overlay');
    if (overlay) overlay.classList.remove('active');
    
    // Re-enable body scrolling
    document.body.classList.remove('overflow-hidden');
  }

  openTrailBoxSystem(catNumber = 1) {
    
    const trailBoxSystem = document.querySelector('.trail-box-system');
    const trailBoxElement = document.querySelector('trail-box');
    
    if (trailBoxSystem) {
      // Store which cat we're editing
      trailBoxSystem.setAttribute('data-editing-cat', catNumber);
      
      // Get the specific cat's data
      const catData = this.submissionData[`cat${catNumber}`];
      const catRecommendations = this.lastCalculatedRecommendations?.cats?.[`cat${catNumber}`];
      
      // Update the trail box header with cat name
      const headerTitle = trailBoxSystem.querySelector('.trail-box-header h3');
      if (headerTitle && catData) {
        headerTitle.textContent = `${catData.fuzzballer_name || `Cat ${catNumber}`}'s trial box`;
      }
      
      // Also update the trail-box-system-header
      const systemHeaderTitle = document.querySelector('.trail-box-system-header h3 .cat-name-display');
      if (systemHeaderTitle && catData) {
        systemHeaderTitle.textContent = catData.fuzzballer_name || `Cat ${catNumber}`;
      }
      
      trailBoxSystem.classList.add('trail-box-system-active');
      
      // Show overlay
      const overlay = document.querySelector('.quiz-modal-overlay');
      if (overlay) overlay.classList.add('active');
      
      // Prevent body scrolling
      document.body.classList.add('overflow-hidden');
      
      // If trail box element has openTrailBox method, call it with cat-specific data
      if (trailBoxElement && trailBoxElement.openTrailBox) {
        // Check if we're in edit mode
        const editMode = window.QuizState.get('editMode');
        
        if (editMode?.active && editMode?.restricted) {
          // In edit mode, just open the trail box without recommendations
          // The trail box will restore products from editMode data
          console.log('Opening trail box in edit mode without recommendations');
          trailBoxElement.openTrailBox(null);
        } else {
          // Normal mode - pass recommendations
          if (catRecommendations) {
            trailBoxElement.openTrailBox(catRecommendations);
          } else if (catData) {
            // If no recommendations exist and we have cat data, calculate them fresh for this cat
            const freshRecommendations = window.QuizCalculator.calculate(catData);
            trailBoxElement.openTrailBox(freshRecommendations);
          } else {
            // No cat data available, open with null
            console.log('Opening trail box without cat data');
            trailBoxElement.openTrailBox(null);
          }
        }
        
        // Update the trail box count after a delay to ensure it's populated
        const numCats = this.submissionData?.catCount || 1;
        if (numCats === 1) {
          setTimeout(() => {
            this.updateSingleCatTrailBoxCount();
          }, 300);
        }
      }

      // Set up extras listeners inside the trail box modal (once) and refresh button states
      this.setupTrailBoxExtrasListeners();
      if (window.QuizExtrasManager) {
        window.QuizExtrasManager.updateTrailBoxExtrasButtons();
      }
    } else {
    }
  }
  
  ensureTrailBoxSelectionsForAllCats(recommendations, trailBoxElement) {
    
    if (!trailBoxElement) {
      return;
    }
    
    // Initialize selections if needed
    if (!trailBoxElement.selections) {
      trailBoxElement.selections = {
        cat1: [],
        cat2: [],
        cat3: [],
        cat4: []
      };
    }
    
    // Get actual cat count from submission data
    const catCount = this.submissionData.catCount || 1;
    
    // Create trail box selections based on quiz answers (with inventory checking)
    if (recommendations.cats) {
      
      // For each cat, create trail box selections using the same logic as bundles
      // IMPORTANT: Only process cats that actually exist based on catCount
      Object.entries(recommendations.cats).forEach(([catKey, catRec]) => {
        const catNumber = parseInt(catKey.replace('cat', ''));
        
        // Skip cats that don't exist based on catCount
        if (catNumber > catCount) {
          return;
        }
        
        
        // The trail box will handle the actual product filtering and selection
        // We just need to trigger the auto-populate for each cat
        if (trailBoxElement && typeof trailBoxElement.autoPopulateFromQuiz === 'function') {
          // Pass the cat-specific recommendations
          trailBoxElement.editingCat = catNumber.toString();
          trailBoxElement.autoPopulateFromQuiz(catRec);
        }
      });
      
      // Save trail selections to localStorage
      if (trailBoxElement.selections) {
        localStorage.setItem('quiz_trail_selections', JSON.stringify(trailBoxElement.selections));
      }
      
      // Reset editing cat
      if (trailBoxElement) {
        trailBoxElement.editingCat = null;
      }
      
      // Update the UI to show actual trail box counts
      if (catCount > 1) {
        this.updateMultiCatTrailBoxCounts();
      } else {
        this.updateSingleCatTrailBoxCount();
      }
    }
  }
  
  autoPopulateTrailBoxesForAllCats(recommendations) {
    
    const trailBoxElement = document.querySelector('trail-box');
    if (!trailBoxElement) {
      return;
    }
    
    // Initialize selections if needed
    if (!trailBoxElement.selections) {
      trailBoxElement.selections = {
        cat1: [],
        cat2: [],
        cat3: [],
        cat4: []
      };
    }
    
    // For each cat, create default trail selections
    if (recommendations.cats) {
      Object.entries(recommendations.cats).forEach(([catKey, catRec]) => {
        const catNumber = catKey.replace('cat', '');
        
        // Clear any existing selections for this cat
        trailBoxElement.selections[catKey] = [];
        
        // Add default trail items based on diet preference
        const defaultTrailItems = this.getDefaultTrailItems(catRec, catNumber);
        trailBoxElement.selections[catKey] = defaultTrailItems;
        
      });
      
    }
  }
  
  getDefaultTrailItems(catRec, catNumber) {
    // Create default trail selections based on cat's diet preference.
    // The wet/both path is dynamic: it sources the available wet variants
    // off the <trail-box> element's data attribute and distributes
    // `2 × multipackSize` cans evenly across them. This (a) scales the
    // multi-cat seeder with the live customizer setting (14 cans at
    // multipack=7, 12 at multipack=6) and (b) automatically retires
    // variants pulled from the customizer — fixing the latent issue where
    // this seeder still referenced the discontinued beef SKU.
    // The dry-only path keeps its hardcoded variant list because the
    // kitten / adult split is encoded in those SKUs and the customizer
    // data attribute doesn't expose age-suitability.
    const trailItems = [];
    const trailBoxElement = document.querySelector('trail-box');

    if (catRec.diet === 'Wet' || catRec.diet === 'Both') {
      const multipackSize = this._getTrailBoxMultipackSize(trailBoxElement);
      const target = multipackSize * 2;
      const wetProducts = this._getTrailBoxProducts(trailBoxElement, 'data-trail-box-cans');
      const distribution = this._distributeEvenly(wetProducts, target);

      distribution.forEach(({ product, quantity }) => {
        if (quantity > 0) {
          trailItems.push({
            id: product.id,
            variantId: product.variant_id || this.getVariantIdForProduct(product.id),
            title: product.title,
            type: 'can',
            quantity: quantity,
            cat_number: catNumber
          });
        }
      });
    } else if (catRec.diet === 'Dry') {
      // Default: 2 dry bags. Target stays at 2 regardless of multipackSize
      // (dry goal is size-independent). Kitten vs adult split encoded in
      // the variant IDs because the customizer doesn't carry age tags on
      // the dry-foods product list.
      const dryProducts = [
        { id: '7252839202948', title: 'Chicken and Salmon w/Sweet Potato | Kitten Food (500g)', type: 'dry', quantity: catRec.isKitten ? 2 : 0 },
        { id: '8732009382164', title: 'Chicken w/Tuna + Salmon (500g)', type: 'dry', quantity: catRec.isKitten ? 0 : 1 },
        { id: '8732009513236', title: 'Turkey & Chicken (500g)', type: 'dry', quantity: catRec.isKitten ? 0 : 1 }
      ];

      // Add dry products with cat number
      dryProducts.forEach(product => {
        if (product.quantity > 0) {
          trailItems.push({
            id: product.id,
            variantId: this.getVariantIdForProduct(product.id),
            title: product.title,
            type: product.type,
            quantity: product.quantity,
            cat_number: catNumber
          });
        }
      });
    }

    return trailItems;
  }
  
  getVariantIdForProduct(productId) {
    // Map product IDs to variant IDs (these would need to be fetched from Shopify in a real implementation)
    const variantMap = {
      '15036968239479': '55398973112695',
      '15037000155511': '55398959088007',
      '15037000188279': '55398956564871',
      '15036900311415': '55398973636983',
      '15037000712567': '55398957416839',
      '15037001105783': '55398955090311',
      '15037001269623': '55398955090295',
      '7252839202948': '41797476319364',
      '8732009382164': '48719674122583',
      '8732009513236': '48719674155351'
    };
    
    return variantMap[productId] || productId;
  }
  
  updateTrailBoxCountsAfterClose() {
    const trailBoxElement = document.querySelector('trail-box');
    if (!trailBoxElement) return;

    const trailBoxSystem = document.querySelector('.trail-box-system');
    const editingCat = trailBoxSystem ? trailBoxSystem.getAttribute('data-editing-cat') : null;

    if (editingCat && trailBoxElement.selections) {
      const catKey = `cat${editingCat}`;
      if (trailBoxElement.selections[catKey]) {
        const totalItems = trailBoxElement.selections[catKey].reduce((sum, product) => sum + product.quantity, 0);
        const catTrailBoxSection = document.querySelector(`.trail-box-section[data-cat="${editingCat}"]`);
        if (catTrailBoxSection) {
          const itemsCountEl = catTrailBoxSection.querySelector('.trail-box-items-count');
          if (itemsCountEl) {
            itemsCountEl.textContent = totalItems;
          }
        }
      }
    } else if (!editingCat && trailBoxElement.selections && trailBoxElement.selections.cat1) {
      const totalItems = trailBoxElement.selections.cat1.reduce((sum, product) => sum + product.quantity, 0);
      const allTrailBoxCounts = document.querySelectorAll('.trail-box-items-count');
      allTrailBoxCounts.forEach(element => {
        if (!element.closest('[data-cat]') && !element.closest('.trail-box-summary-header')) {
          element.textContent = totalItems;
        }
      });
    }

    const numCats = this.submissionData.catCount || 1;
    if (numCats > 1) {
      this.updateTrailBoxTotalItems();
    }
  }

  updateCheckoutBarForTab(tabName) {
    const checkoutBarTotal = document.querySelector('.checkout-bar-row:last-child span:last-child');
    const checkoutBarText = document.querySelector('.checkout-bar-text');
    
    if (tabName === 'trial') {
      // Update for trial tab
      const numberOfCats = this.submissionData.catCount || 1;
      let totalTrialPrice = window.QuizConstants.FOOD.TRIAL_BOX_PRICE * numberOfCats;
      
      // Add extras total from all cats
      if (window.QuizExtrasManager) {
        const extrasTotal = window.QuizExtrasManager.getTotalPrice(); // Gets total across all cats
        totalTrialPrice += extrasTotal;
      }
      
      // Update total price
      const trialCheckoutTotal = document.querySelector('.trial-checkout-total');
      if (trialCheckoutTotal) {
        trialCheckoutTotal.textContent = `£${totalTrialPrice.toFixed(2)}`;
      }
      
      // Update checkout bar text for trial
      if (checkoutBarText) {
        const bundlePrice = document.querySelector('.bundle-system-price');
        if (bundlePrice) {
          checkoutBarText.innerHTML = `In 12 days, if your cat loves it, you will pay <span class="bundle-system-price">${bundlePrice.textContent}</span>/month. Cancel, pause or edit anytime. No commitments!`;
        }
      }
    } else if (tabName === 'monthly') {
      // For monthly tab, DON'T update the trial-checkout-total
      // The trial price should remain unchanged

      // Update checkout bar text for monthly
      if (checkoutBarText) {
        const bundlePrice = document.querySelector('.bundle-system-price');
        if (bundlePrice) {
          checkoutBarText.innerHTML = `In 12 days, if your cat loves it, you will pay <span class="bundle-system-price">${bundlePrice.textContent}</span>/month. Cancel, pause or edit anytime. No commitments!`;
        }
      }
    }

    // Keep free shipping progress bar in sync with the displayed totals
    this.updateFreeShippingBar();
  }

  handlePlanTabChange(clickedTab) {
    const tabName = clickedTab.dataset.tab;
    
    // Update active tab
    const allTabs = document.querySelectorAll('.plan-tabs .tab');
    allTabs.forEach(tab => {
      tab.classList.remove('active');
    });
    clickedTab.classList.add('active');
    
    // Show corresponding content
    const allTabContents = document.querySelectorAll('.tab-content[data-tab-content]');
    allTabContents.forEach(content => {
      if (content.dataset.tabContent === tabName) {
        content.style.display = 'block';
      } else {
        content.style.display = 'none';
      }
    });
    
    // Update checkout bar based on active tab
    this.updateCheckoutBarForTab(tabName);
    
    // If switching to monthly tab, update the bundle price to reflect current selections
    if (tabName === 'monthly') {
      const bundleSystem = document.querySelector('quiz-bundle');
      if (bundleSystem && bundleSystem.selections && bundleSystem.selections.length > 0) {
        const actualPrice = bundleSystem.selections.reduce((sum, item) => sum + (item.price * item.quantity), 0);

        // Update bundle price displays
        const bundleTotalPriceEls = document.querySelectorAll('.bundle-total-price');
        bundleTotalPriceEls.forEach(price => {
          price.textContent = `£${actualPrice.toFixed(2)}`;
        });

        // Specifically update monthly tab price
        const monthlyTabPrice = document.querySelector('.tab[data-tab="monthly"] .bundle-total-price');
        if (monthlyTabPrice) {
          monthlyTabPrice.textContent = `£${actualPrice.toFixed(2)}`;
        }
      }
    }

    // Tab switch may have changed the displayed bundle total — refresh bar
    this.updateFreeShippingBar();
  }

  validateStep(step) {
    return window.QuizValidators.validateStep(step, this.submissionData);
  }
  
  updateSkipButtonState() {
    const skipBtn = document.querySelector('.quiz-skip-btn');
    if (!skipBtn) return;
    
    // Always enable skip button - validation happens in the popup
    skipBtn.disabled = false;
    skipBtn.classList.remove('disabled');
    skipBtn.setAttribute('title', 'Skip to recommendations');
  }
  
  updateQuizDescription() {
    const descriptionContainer = document.querySelector('.quiz-description');
    const descriptionText = descriptionContainer?.querySelector('p');
    
    if (!descriptionContainer || !descriptionText) return;
    
    // Get the description for the current step from global settings
    const stepDescriptions = window.quizStepDescriptions || {};
    const currentDescription = stepDescriptions[`step_${this.currentStep}_description`] || '';
    
    if (currentDescription) {
      descriptionText.textContent = currentDescription;
      descriptionContainer.style.display = 'block';
    } else {
      descriptionContainer.style.display = 'none';
    }
  }

  nextStep() {
    if (this.validateStep(this.currentStep) && this.currentStep < this.totalSteps) {
      let next = this.currentStep + 1;
      while (next < this.totalSteps && this.shouldSkipStep(next)) {
        next++;
      }
      this.currentStep = next;
      this.updateStepDisplay();
      this.updateProgressDisplay();
    } else if (!this.validateStep(this.currentStep)) {
      // Validation failed - scroll to first unanswered question
      this.scrollToFirstUnansweredQuestion();
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      let prev = this.currentStep - 1;
      while (prev > 1 && this.shouldSkipStep(prev)) {
        prev--;
      }
      this.currentStep = prev;
      this.updateStepDisplay();
      this.updateProgressDisplay();
    }
  }

  async skipToEnd() {
    // Check which required fields are missing
    const missingFields = this.getMissingRequiredFields();

    if (missingFields.length > 0) {
      // Show popup to collect missing data
      this.showSkipDataPopup(missingFields);
    } else {
      // All required fields present, proceed with skip
      if (await fuzzballConfirm('Are you sure you want to skip the remaining questions? We\'ll use default values for better recommendations.', { title: 'Skip remaining questions?', confirmText: 'Skip' })) {
        this.proceedWithSkip();
      }
    }
  }
  
  getMissingRequiredFields() {
    const required = [];
    const catData = this.submissionData.cat1 || this.submissionData;
    
    // Check cat name
    if (!catData.fuzzballer_name) {
      required.push('fuzzballer_name');
    }
    
    // Check food preference
    if (!catData.wet_dry_preference) {
      required.push('wet_dry_preference');
    }
    
    return required;
  }
  
  showSkipDataPopup(missingFields) {
    // Lock body scroll
    window.QuizUtils.DOM.lockBodyScroll();
    
    // Create popup overlay
    const overlay = document.createElement('div');
    overlay.className = 'quiz-modal-overlay';
    
    // Create popup container
    const popup = document.createElement('div');
    popup.className = 'skip-popup-system';
    popup.innerHTML = `
      <div class="skip-popup-header">
        <h3 class="cm-section-header__heading--squiggle">What do you feed ${this.submissionData.cat1?.fuzzballer_name || 'your cat'}?
          <span class="cm-section-header__squiggle">
            <svg class="colour--yellow" width="296" height="37" viewBox="0 0 296 37" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 26.4064L23.0541 20.6151C30.2573 17.4195 37.8864 15.2865 45.7025 14.2828L69.2699 11.2567C76.4412 10.3359 83.7033 10.3743 90.8644 11.3711L134.458 17.4389C140.114 18.2262 145.836 18.4162 151.532 18.006L223.461 12.8248L276.747 15.5584L285.535 16.9312" stroke="#F5E295" stroke-width="20" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </h3>
        <p class="skip-popup-subtitle Md_Body_Regular">Save time and let us pick your order for you</p>
        <button type="button" class="skip-popup-close" aria-label="Close">
          <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="46" height="46" rx="8" fill="white"/>
            <path d="M18 28L28 18M18 18L28 28" stroke="#036ED1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <div class="skip-popup-content">
        <form id="skip-data-form">
          ${this.buildSkipFormFields(missingFields)}
        </form>
        <button type="button" class="skip-continue-btn button button--primary" disabled>Continue</button>
      </div>
    `;
    
    // Add to DOM
    document.body.appendChild(overlay);
    document.body.appendChild(popup);
    
    // Trigger animation
    requestAnimationFrame(() => {
      overlay.classList.add('active');
      popup.classList.add('skip-popup-active');
    });
    
    // Setup event listeners
    this.setupSkipPopupListeners(popup, overlay, missingFields);
  }
  
  buildSkipFormFields(missingFields) {
    let html = '';
    
    // Get configured images from data attributes
    const container = document.querySelector('.fuzzball-quiz-container');
    const wetImage = container?.dataset.skipPopupWetImage || '';
    const dryImage = container?.dataset.skipPopupDryImage || '';
    const mixImage = container?.dataset.skipPopupMixImage || '';
    
    missingFields.forEach(field => {
      if (field === 'fuzzballer_name') {
        html += `
          <div class="input-group">
            <label for="skip-cat-name" class="Md_Body_Bold">What's your cat's name?</label>
            <input type="text" id="skip-cat-name" name="fuzzballer_name" required>
          </div>
        `;
      } else if (field === 'wet_dry_preference') {
        // Check if we need a title (when showing with other fields)
        const needsTitle = missingFields.includes('fuzzballer_name');
        
        html += `
          <div class="input-group">
            ${needsTitle ? '<label class="Md_Body_Bold">What type of food does your cat prefer?</label>' : ''}
            <div class="radio-group skip-food-preference">
              <label class="radio-option">
                <div class="option-image"${wetImage ? ` style="background-image: url('${wetImage}'); background-size: cover; background-position: center;"` : ''}></div>
                <input type="radio" name="wet_dry_preference" value="Wet" required>
                <span class="radio-text">Wet Food Only</span>
              </label>
              <label class="radio-option">
                <div class="option-image"${dryImage ? ` style="background-image: url('${dryImage}'); background-size: cover; background-position: center;"` : ''}></div>
                <input type="radio" name="wet_dry_preference" value="Dry" required>
                <span class="radio-text">Dry Food Only</span>
              </label>
              <label class="radio-option">
                <div class="option-image"${mixImage ? ` style="background-image: url('${mixImage}'); background-size: cover; background-position: center;"` : ''}></div>
                <input type="radio" name="wet_dry_preference" value="Both" required>
                <span class="radio-text">Mix of Both</span>
              </label>
            </div>
          </div>
        `;
      }
    });
    
    return html;
  }
  
  setupSkipPopupListeners(popup, overlay, missingFields) {
    const form = popup.querySelector('#skip-data-form');
    const continueBtn = popup.querySelector('.skip-continue-btn');
    const closeBtn = popup.querySelector('.skip-popup-close');
    
    // Close button handler
    closeBtn.addEventListener('click', () => {
      this.closeSkipPopup(popup, overlay);
    });
    
    // Form validation
    const validateForm = () => {
      const isValid = form.checkValidity();
      continueBtn.disabled = !isValid;
    };
    
    // Add input listeners
    form.addEventListener('input', validateForm);
    form.addEventListener('change', validateForm);
    
    // Continue button handler
    continueBtn.addEventListener('click', () => {
      if (form.checkValidity()) {
        // Collect form data
        const formData = new FormData(form);
        
        // Update submission data
        formData.forEach((value, key) => {
          if (this.submissionData.cat1) {
            this.submissionData.cat1[key] = value;
          } else {
            this.submissionData[key] = value;
          }
        });
        
        // Save data
        window.QuizState.save('submissionData', this.submissionData);
        
        // Close popup and proceed
        this.closeSkipPopup(popup, overlay);
        
        // Now proceed with skip
        this.proceedWithSkip();
      }
    });
  }
  
  closeSkipPopup(popup, overlay) {
    popup.classList.remove('skip-popup-active');
    overlay.classList.remove('active');
    
    // Unlock body scroll
    window.QuizUtils.DOM.unlockBodyScroll();
    
    setTimeout(() => {
      popup.remove();
      overlay.remove();
    }, 300);
  }
  
  proceedWithSkip() {
    // Mark that we skipped
    this.skippedQuiz = true;
    
    // Fill defaults first
    this.fillDefaults();
    
    // Go directly to step 7 (results page)
    this.currentStep = 7;
    
    // Calculate recommendations for all cats
    const recommendations = this.calculateAllCatsRecommendations();
    
    // Store recommendations
    window.QuizState.updateRecommendations(recommendations);
    
    // Mark quiz as completed
    window.QuizState.markQuizComplete();
    
    // Update display
    this.updateStepDisplay();
    this.updateProgressDisplay();
    
    // Update step 7 content with recommendations
    this.updateStep7Content(recommendations);
    
    // Initialize bundle system with recommendations
    this.initializeBundleSystemWithRecommendations(recommendations);
    
    // Hide the "Add Another Cat" button on skipped quiz
    const addCatBtn = document.querySelector('.add-another-cat');
    if (addCatBtn) {
      addCatBtn.style.display = 'none';
    }
    
    // Ensure trail box gets updated
    setTimeout(() => {
      const trailBoxElement = document.querySelector('trail-box');
      if (trailBoxElement && trailBoxElement.applyQuizRecommendations) {
        trailBoxElement.applyQuizRecommendations(recommendations);
      }
      
      // Update trail box count
      this.updateSingleCatTrailBoxCount();
    }, 300);
  }

  fillDefaults() {
    // Get cat data reference
    const catData = this.submissionData.cat1 || this.submissionData;
    
    const defaults = {
      breed: 'I don\'t know',
      age_range: 'Adult (1-8 years)',
      wet_dry_preference: 'Both',
      cans_per_day: '2',
      health_concerns_exists: 'No',
      health_concerns: '',
      lifestyle: 'Both',
      disliked_flavors: []
    };

    // Fill in missing cat-specific values
    Object.keys(defaults).forEach(key => {
      if (!catData[key] || 
          (Array.isArray(catData[key]) && catData[key].length === 0)) {
        catData[key] = defaults[key];
      }
    });
    
    // Ensure cat data is properly stored
    if (!this.submissionData.cat1) {
      this.submissionData.cat1 = catData;
    }
    
    // Fill in missing global values
    if (!this.submissionData.customer_name) {
      this.submissionData.customer_name = '';
    }
    if (!this.submissionData.customer_email) {
      this.submissionData.customer_email = '';
    }

    window.QuizState.save('submissionData', this.submissionData);
  }

  // GA4 quiz funnel events. Called from updateStepDisplay() inside the
  // existing previousStep !== currentStep gate, so this only fires on real
  // step transitions (initial render, nextStep, prevStep, goToStep,
  // edit-mode jumps) and never on intra-step re-renders.
  fireQuizAnalytics() {
    const stepNumber = this.currentStep;
    const stepName = QUIZ_STEP_NAMES[stepNumber] || ('step_' + stepNumber);
    const direction = (this.previousStep != null && this.previousStep > stepNumber)
      ? 'backward'
      : 'forward';
    const catCount = (this.submissionData && this.submissionData.catCount) || 1;

    const source = this.entryMode || 'fresh';

    quizGtagPush('event', 'quiz_step_viewed', {
      quiz_step_number: stepNumber,
      quiz_step_name: stepName,
      cat_count: catCount,
      direction: direction,
      event_source: source
    });

    // quiz_started: fire once per genuine (non-edit) session, on the FIRST step
    // displayed — whatever step that is (BYB entrants started at step 2 until
    // 19 Oct 2026, which the old step===1 gate missed). Edit re-entries
    // are excluded (they're not new starts).
    if (source !== 'edit' && !this.quizStartedFired) {
      this.quizStartedFired = true;
      quizGtagPush('event', 'quiz_started', {
        cat_count: catCount,
        entry_step: stepNumber,
        event_source: source
      });
    }

    // quiz_completed: only a genuine (non-edit) session reaching the plan summary
    // counts. Edit-mode re-entries to step 7 no longer inflate completions.
    if (stepNumber === 7 && source !== 'edit' && !this.quizCompletedFired) {
      this.quizCompletedFired = true;
      quizGtagPush('event', 'quiz_completed', {
        cat_count: catCount,
        event_source: source
      });
    }
  }

  updateStepDisplay() {
    // Log step change
    console.log(`📍 Step ${this.currentStep}: Quiz data:`, this.submissionData);
    if (this.currentStep === 7) {
      const debugRecs = this.calculateAllCatsRecommendations();
      console.log('━━━ QUIZ 2.1 DEBUG (Step 7) ━━━');
      console.log(JSON.stringify({ submissionData: this.submissionData, recommendations: debugRecs }, null, 2));
      console.log('━━━ END DEBUG ━━━');
    }
    
    // Hide all steps
    document.querySelectorAll('.quiz-step').forEach(step => {
      step.classList.remove('active');
    });
    
    // Show current step for all cats (steps 1-5) with per-cat visibility
    if (this.currentStep >= 1 && this.currentStep <= 5) {
      const catCount = this.submissionData.catCount || 1;
      for (let i = 1; i <= catCount; i++) {
        const catStepEl = document.querySelector(`.quiz-step[data-step="${this.currentStep}"][data-cat="${i}"]`);
        if (catStepEl && this.isCatVisibleOnStep(i, this.currentStep)) {
          catStepEl.classList.add('active');
        }
      }

      // Toggle lifestyle + age section visibility on step 4
      if (this.currentStep === 4) {
        for (let i = 1; i <= catCount; i++) {
          const catStepEl = document.querySelector(`.quiz-step[data-step="4"][data-cat="${i}"]`);
          if (!catStepEl) continue;
          const catData = this.submissionData[`cat${i}`];
          const pref = catData?.wet_dry_preference;
          const lifestyleSection = catStepEl.querySelector('.lifestyle-section');
          if (lifestyleSection) {
            lifestyleSection.style.display = (pref === 'Dry' || pref === 'Both') ? '' : 'none';
          }
          const ageSection = catStepEl.querySelector('.age-section');
          if (ageSection) {
            ageSection.style.display = (pref === 'Dry' || pref === 'Both') ? '' : 'none';
          }
        }
      }

      // Show/hide add another cat button
      const addCatBtn = document.querySelector('.add-another-cat');
      if (addCatBtn) {
        // Never show add cat button if quiz was skipped
        addCatBtn.style.display = (this.currentStep === 1 && catCount < 3 && !this.skippedQuiz) ? 'block' : 'none';
      }
    } else {
      // For steps 6-7, just show the single step
      const currentStepEl = document.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
      if (currentStepEl) {
        currentStepEl.classList.add('active');
      }

      // Update cat name displays on step 6 with all cat names
      if (this.currentStep === 6) {
        this.updateSlide6CatNames();
      }
    }
    
    // Update data attribute on quiz container
    const quizContainer = document.querySelector('.fuzzball-quiz-container');
    if (quizContainer) {
      quizContainer.setAttribute('data-current-step', this.currentStep);
    }
    
    // Add quiz-completed class and remove overflow-hidden when on step 7
    if (this.currentStep === 7) {
      // ── Quiz 2.1 Debug — fires on step 7 display ──
      const debugRecs = this.calculateAllCatsRecommendations();
      console.log('━━━ QUIZ 2.1 DEBUG (Step 7) ━━━');
      console.log(JSON.stringify({
        submissionData: this.submissionData,
        recommendations: debugRecs
      }, null, 2));
      console.log('━━━ END DEBUG ━━━');

      document.body.classList.add('quiz-completed');
      document.body.classList.remove('overflow-hidden');
    } else {
      document.body.classList.remove('quiz-completed');
    }
    
    // Update visual content (videos) for current step
    this.updateVisualContent();

    // Sync vet messages with any pre-selected answers on the active slides
    this.syncVetMessages();

    // Pre-select quantity on slide 3 based on food preference from slide 2
    if (this.currentStep === 3) {
      const catCount = this.submissionData.catCount || 1;
      for (let i = 1; i <= catCount; i++) {
        const catData = this.submissionData[`cat${i}`];
        if (!catData || catData.cans_per_day) continue; // already has a selection

        const pref = catData.wet_dry_preference;
        let preSelectValue = null;
        if (pref === 'Wet') preSelectValue = '2';
        else if (pref === 'Both') preSelectValue = '1';

        if (!preSelectValue) continue;

        const catStepEl = document.querySelector(`.quiz-step[data-step="3"][data-cat="${i}"]`);
        if (!catStepEl) continue;

        const radio = catStepEl.querySelector(`input[name="cans_per_day"][value="${preSelectValue}"]`);
        if (radio && !radio.checked) {
          radio.checked = true;
          window.QuizState.updateCatData(i, 'cans_per_day', preSelectValue);
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Show extras popup when reaching step 4 (health/lifestyle)
    if (this.currentStep === 4 && !this.extrasPopupShown) {
      const catCount = this.submissionData.catCount || 1;
      if (catCount > 1) {
        // Open once with cat tabs — default to cat 1
        this.showExtrasPopup(1);
      } else {
        this.showExtrasPopup();
        this.extrasPopupShown = true;
      }
    }

    // Validate current step
    const isCurrentStepValid = this.validateStep(this.currentStep);

    // Update navigation buttons based on step
    this.updateNavigationButtons(isCurrentStepValid);
    
    // Auto-scroll behavior
    // Skip step 7 which has special layout
    // Skip if we're updating from handleInputChange to prevent double scroll
    const isFirstVisit = !this.visitedSteps.has(this.currentStep);
    
    console.warn('[Scroll Debug] Step display update:', {
      currentStep: this.currentStep,
      isFirstVisit: isFirstVisit,
      visitedSteps: Array.from(this.visitedSteps),
      skipAutoScroll: this.skipAutoScroll
    });
    
    if (isFirstVisit) {
      // Mark this step as visited
      this.visitedSteps.add(this.currentStep);
    }
    
    if (this.currentStep === 6 && !this.skipAutoScroll) {
      // Scroll to top for step 6
      setTimeout(() => {
        const isMobile = window.innerWidth <= 768;
        const scrollContainer = isMobile ?
          document.querySelector('.quiz-content') :
          document.querySelector('.quiz-main-content');

        if (scrollContainer) {
          scrollContainer.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else if (this.currentStep === 7 && !this.skipAutoScroll) {
      // Scroll to top for step 7
      setTimeout(() => {
        const isMobile = window.innerWidth <= 768;
        const scrollContainer = isMobile ?
          document.querySelector('.quiz-content') :
          document.querySelector('.quiz-main-content');

        if (scrollContainer) {
          scrollContainer.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else if (this.currentStep !== 7 && !this.skipAutoScroll) {
      // For all other steps (except 7)
      if (isFirstVisit) {
        // First visit: scroll to top
        setTimeout(() => {
          const isMobile = window.innerWidth <= 768;
          let scrollContainer;
          
          if (isMobile) {
            // On mobile, quiz-content is the scroll container
            scrollContainer = document.querySelector('.quiz-content');
          } else {
            // On desktop, quiz-main-content is the scroll container
            scrollContainer = document.querySelector('.quiz-main-content');
          }
          
          if (scrollContainer) {
            scrollContainer.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          } else {
          }
        }, 100);
      } else {
        // Subsequent visits: no longer auto-scroll
      }
    }
    
    // Only update animations if the step has actually changed
    if (this.previousStep !== this.currentStep) {
      // GA4 quiz funnel events — direction is derived from the previous
      // step value, so fire before previousStep is updated below.
      this.fireQuizAnalytics();

      // Initialize or update Lottie animations
      this.updateLottieAnimation();
      this.updateBallAnimation();

      // Update previous step tracker after animations
      this.previousStep = this.currentStep;
    }
    
    // Update skip button state whenever we display a step
    this.updateSkipButtonState();
    
    // Update quiz description for the current step
    this.updateQuizDescription();
    
    // Update visual state for all inputs after step display
    setTimeout(() => {
      this.updateAllInputStates();
    }, 50); // Small delay to ensure DOM is updated
  }
  
  goToStep(step) {
    this.currentStep = step;
    this.updateStepDisplay();
    this.updateProgressDisplay();
  }

  updateNavigationButtons(isCurrentStepValid) {
    const navBackBtn = document.querySelector('.quiz-nav-back');
    const navForwardBtn = document.querySelector('.quiz-nav-forward');
    const bottomButtons = document.querySelector('.quiz-bottom-buttons');
    const nextBtn = document.querySelector('.quiz-next-btn');

    // Special handling for step 6 (lead capture)
    if (this.currentStep === 6) {
      if (navBackBtn) {
        navBackBtn.style.display = 'flex';
        navBackBtn.disabled = false;
      }
      // Top-bar forward arrow mirrors the bottom .quiz-next-btn at step 6:
      // visible, disabled until name+email are valid. The step-6 submit button
      // now mirrors the same validity-only gate, so all three stay navigable
      // after a prior submit; the user can walk forward to step 7 with the
      // existing capture without re-submitting to Klaviyo.
      // validateStep6 (fired below) re-asserts the actual disabled state.
      if (navForwardBtn) {
        navForwardBtn.style.display = 'flex';
      }
      if (bottomButtons) bottomButtons.style.display = 'flex';

      // Hide skip button on step 6
      const skipBtn = document.querySelector('.quiz-skip-btn');
      if (skipBtn) skipBtn.style.display = 'none';

      // validateStep6 (fired below) is the single source of the step-6 button's
      // disabled state — gated on validity only. A prior Klaviyo submit no
      // longer force-disables the button; the re-submit guard is the
      // handleStep6Submission() short-circuit.
      setTimeout(() => this.validateStep6(), 100);
      return;
    }

    // Special handling for step 7 (plan summary)
    if (this.currentStep === 7) {
      // Keep top-bar back arrow visible on step 7 — same single-arrow
      // treatment as steps 2-6. Step 7 has its own .step-7-checkout-bar,
      // so the bottom-buttons row is still hidden.
      if (navBackBtn) {
        navBackBtn.style.display = 'flex';
        navBackBtn.disabled = false;
      }
      // Forward arrow is hidden on step 7 — nothing to navigate to.
      if (navForwardBtn) navForwardBtn.style.display = 'none';
      if (bottomButtons) bottomButtons.style.display = 'none';
      return;
    }

    // Update top navigation buttons
    if (navBackBtn) {
      navBackBtn.style.display = 'flex';
      navBackBtn.disabled = this.currentStep === 1;
    }

    // Top-bar forward arrow mirrors the bottom .quiz-next-btn disabled state
    // on steps 1-5, so validation is identical regardless of which arrow the
    // user clicks. Step 1's back-arrow stays disabled per existing rule, but
    // the forward arrow follows the same valid/invalid rule as steps 2-5.
    if (navForwardBtn) {
      navForwardBtn.style.display = 'flex';
      navForwardBtn.disabled = !isCurrentStepValid;
    }

    // Update bottom navigation buttons
    if (bottomButtons) {
      bottomButtons.style.display = 'flex';
    }
    
    // Show skip button on all steps except 6
    const skipBtn = document.querySelector('.quiz-skip-btn');
    if (skipBtn) skipBtn.style.display = 'flex';
    
    if (nextBtn) {
      nextBtn.disabled = !isCurrentStepValid;
      
      if (this.currentStep === this.totalSteps) {
        nextBtn.textContent = 'Show me my plan';
        nextBtn.setAttribute('data-action', 'complete');
      } else {
        nextBtn.textContent = 'Next';
        nextBtn.setAttribute('data-action', 'next');
      }
    }
  }

  isQuestionComplete(inputGroup) {
    // Check if all required inputs in this question are filled
    const inputs = inputGroup.querySelectorAll('input, select, textarea');
    
    for (const input of inputs) {
      // Skip non-required inputs
      if (!input.hasAttribute('required') && !input.classList.contains('required')) {
        continue;
      }
      
      if (input.type === 'radio') {
        // For radio buttons, check if any in the group is selected
        const radioGroup = inputGroup.querySelectorAll(`input[name="${input.name}"]`);
        const isSelected = Array.from(radioGroup).some(radio => radio.checked);
        if (!isSelected) return false;
      } else if (input.type === 'checkbox') {
        // Checkboxes are usually optional, so we don't check them
        continue;
      } else if ((input.type === 'text' || input.type === 'email' || input.type === 'number' || 
                  input.tagName === 'SELECT' || input.tagName === 'TEXTAREA') && !input.value) {
        return false;
      }
    }
    
    return true;
  }

  scrollToNextQuestion(currentInputGroup) {
    // Don't auto-scroll on steps 6 and 7 (special layouts)
    if (this.currentStep === 6 || this.currentStep === 7) return;
    
    // Don't scroll if we're in edit mode
    const editMode = window.QuizState.get('editMode');
    if (editMode?.active) return;
    
    // Get ALL active steps (including multiple cats)
    const activeSteps = document.querySelectorAll(`.quiz-step[data-step="${this.currentStep}"].active`);
    if (!activeSteps.length) return;
    
    // Build a combined list of all elements across all active steps (for multi-cat support)
    let allElements = [];
    let currentStepForInput = null;
    
    activeSteps.forEach(step => {
      const stepElements = Array.from(step.querySelectorAll('h2.Md_Body_Bold, h3.Md_Body_Bold, .input-group'));
      stepElements.forEach(el => {
        // Store which step each element belongs to
        el._parentStep = step;
        allElements.push(el);
        
        // Check if current input group is in this step
        if (el === currentInputGroup) {
          currentStepForInput = step;
        }
      });
    });
    
    // Find current question's index in the combined list
    let currentIndex = -1;
    let foundCurrent = false;
    
    for (let i = 0; i < allElements.length; i++) {
      if (allElements[i] === currentInputGroup) {
        // Found our current input group
        currentIndex = i;
        foundCurrent = true;
        break;
      }
    }
    
    if (!foundCurrent) return;
    
    // First, look for the next incomplete question AFTER the current one
    let nextQuestionHeading = null;
    let nextInputGroup = null;
    
    for (let i = currentIndex + 1; i < allElements.length; i++) {
      const element = allElements[i];
      
      // If it's a heading, check if the next element is an input group
      if ((element.tagName === 'H2' || element.tagName === 'H3') && element.classList.contains('Md_Body_Bold')) {
        const nextElement = allElements[i + 1];
        if (nextElement && nextElement.classList.contains('input-group')) {
          // Check if this question is incomplete
          if (!this.isQuestionComplete(nextElement)) {
            nextQuestionHeading = element;
            nextInputGroup = nextElement;
            break;
          }
          // Skip the input group in next iteration since we've checked it
          i++;
        }
      }
    }
    
    // If no incomplete questions found after current position, check for any skipped questions BEFORE
    if (!nextQuestionHeading) {
      for (let i = 0; i < currentIndex; i++) {
        const element = allElements[i];
        
        // If it's a heading, check if the next element is an input group
        if ((element.tagName === 'H2' || element.tagName === 'H3') && element.classList.contains('Md_Body_Bold')) {
          const nextElement = allElements[i + 1];
          if (nextElement && nextElement.classList.contains('input-group')) {
            // Check if this question is incomplete
            if (!this.isQuestionComplete(nextElement)) {
              nextQuestionHeading = element;
              nextInputGroup = nextElement;
              break;
            }
            // Skip the input group in next iteration since we've checked it
            i++;
          }
        }
      }
    }
    
    // If we found a next question, scroll to it
    if (nextQuestionHeading && nextInputGroup) {
      const isMobile = window.innerWidth <= 768;
      const scrollContainer = isMobile ? 
        document.querySelector('.quiz-content') : 
        document.querySelector('.quiz-main-content');
      
      if (!scrollContainer) return;
      
      // Calculate scroll position
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetRect = nextQuestionHeading.getBoundingClientRect();
      
      // Scroll to heading with 80px offset
      const scrollPosition = scrollContainer.scrollTop + (targetRect.top - containerRect.top) - 80;
      
      // Delay slightly for smoother experience
      setTimeout(() => {
        scrollContainer.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
        
        // Focus on text inputs after scrolling (not radio buttons to avoid accidental selection)
        const textInput = nextInputGroup.querySelector('input[type="text"], input[type="number"], input[type="email"], select');
        if (textInput) {
          setTimeout(() => {
            textInput.focus();
          }, 400); // Wait for scroll to complete
        }
      }, 300); // Small delay before scrolling
    }
  }

  scrollToFirstUnansweredQuestion() {
    // Don't scroll on step 6
    if (this.currentStep === 6) return;
    
    const activeSteps = document.querySelectorAll('.quiz-step.active');
    if (!activeSteps.length) return;
    
    const isMobile = window.innerWidth <= 768;
    const scrollContainer = isMobile ? 
      document.querySelector('.quiz-content') : 
      document.querySelector('.quiz-main-content');
    if (!scrollContainer) return;

    // Find the first unanswered question across all active steps
    let targetElement = null;
    let targetInputGroup = null;

    // Search through all active steps in order
    for (const activeStep of activeSteps) {
      const elements = activeStep.querySelectorAll('h2, h3, .input-group');
      
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        let inputGroupToCheck = null;
        let headingElement = null;
        
        // If it's a heading, check the next element for input-group
        if (element.tagName === 'H2' || element.tagName === 'H3') {
          headingElement = element;
          const nextElement = elements[i + 1];
          if (nextElement && nextElement.classList.contains('input-group')) {
            inputGroupToCheck = nextElement;
          }
        } 
        // If it's directly an input-group
        else if (element.classList.contains('input-group')) {
          inputGroupToCheck = element;
          // Look for previous heading
          if (i > 0 && (elements[i-1].tagName === 'H2' || elements[i-1].tagName === 'H3')) {
            headingElement = elements[i-1];
          }
        }
        
        if (inputGroupToCheck) {
          // Skip if the input group is inside a hidden health-concerns-dropdown
          const healthDropdownParent = inputGroupToCheck.closest('.health-concerns-dropdown');
          if (healthDropdownParent && healthDropdownParent.style.display === 'none') {
            continue;
          }
          
          const inputs = inputGroupToCheck.querySelectorAll('input, select, textarea');
          let needsAnswer = false;
          
          // Check if any input in this group needs an answer
          for (const input of inputs) {
            // Skip breed-unknown checkbox as it's just a UI helper
            if (input.name === 'breed-unknown' || input.name?.includes('_breed-unknown')) {
              continue;
            }
            
            // Skip disliked_flavors on step 5 - it's optional
            if (this.currentStep === 5 && (input.name === 'disliked_flavors' || input.name?.includes('disliked_flavors'))) {
              continue;
            }
            
            if (input.type === 'radio' || input.type === 'checkbox') {
              const groupName = input.name;
              const checked = inputGroupToCheck.querySelectorAll(`input[name="${groupName}"]:checked`).length;
              if (checked === 0) {
                needsAnswer = true;
                break;
              }
            } else if ((input.tagName === 'SELECT' || input.type === 'text' || input.type === 'email' || 
                       input.type === 'number' || input.tagName === 'TEXTAREA') && 
                       !input.value && (input.hasAttribute('required') || input.classList.contains('required'))) {
              needsAnswer = true;
              break;
            }
          }
          
          if (needsAnswer) {
            // Found the first unanswered question
            // Always scroll to the input group, not the heading
            targetElement = inputGroupToCheck;
            targetInputGroup = inputGroupToCheck;
            break;
          }
        }
      }
      
      if (targetElement) break;
    }
    
    // If we found an unanswered question, scroll to it
    if (targetElement) {
      // Calculate the scroll position
      let scrollPosition = 0;
      let currentElement = targetElement;
      
      
      // Use getBoundingClientRect for accurate positioning
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();
      
      // Calculate scroll position: current scroll + difference between element and container - 80px offset
      scrollPosition = scrollContainer.scrollTop + (targetRect.top - containerRect.top) - 80;

      // On short viewports the scroll window can be shorter than target + 80px,
      // which leaves the input group clipped under the bottom nav card (it reads
      // as a pop-up covering the field). Scroll further if needed so the whole
      // input group clears the bottom edge, with a small breathing gap.
      const bottomClearPosition = scrollContainer.scrollTop + (targetRect.bottom - containerRect.bottom) + 16;
      if (bottomClearPosition > scrollPosition) {
        scrollPosition = bottomClearPosition;
      }


      // Smooth scroll to the target
      scrollContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
      
      
      // Focus text inputs after scrolling completes
      // Check if the target element contains a text input
      const textInput = targetElement.querySelector('input[type="text"], input[type="number"], input[type="email"]');
      if (textInput) {
        // Small delay to ensure scroll completes before focus. preventScroll
        // stops the browser re-scrolling the input to the clip edge, which
        // would undo the bottom-clearance applied above on short viewports.
        setTimeout(() => {
          textInput.focus({ preventScroll: true });
          // Layout can shift while the smooth scroll runs (cloned step content
          // settling), leaving the input clipped under the bottom nav card on
          // short viewports. Re-measure and nudge so it fully clears the edge.
          const settledContainer = scrollContainer.getBoundingClientRect();
          const settledTarget = targetElement.getBoundingClientRect();
          if (settledTarget.bottom > settledContainer.bottom - 8) {
            scrollContainer.scrollTop += (settledTarget.bottom - settledContainer.bottom) + 16;
          }
        }, 400); // Slightly longer than scroll duration
      }
    }
  }

  updateLottieAnimation() {
    // Check if we're on step 6 (email/name step)
    const quizContainer = document.querySelector('.fuzzball-quiz-container');
    const currentStep = quizContainer ? quizContainer.getAttribute('data-current-step') : null;
    
    console.log('🎊 updateLottieAnimation called:', {
      currentStep,
      lottieAvailable: typeof lottie !== 'undefined',
      confettiUrl: window.confettiAnimationUrl,
      animationExists: !!this.lottieAnimation
    });
    
    if (currentStep === '6' && typeof lottie !== 'undefined') {
      // Initialize Lottie animation if not already done
      if (!this.lottieAnimation) {
        const animationContainer = document.getElementById('quiz-confetti-animation');
        
        if (animationContainer) {
          const animationPath = window.confettiAnimationUrl || '/assets/Confetti.json';
          
          try {
            this.lottieAnimation = lottie.loadAnimation({
              container: animationContainer,
              renderer: 'svg',
              loop: true,
              autoplay: true,
              path: animationPath
            });
            
            // Add event listeners for debugging
            this.lottieAnimation.addEventListener('data_ready', () => {
            });
            
            this.lottieAnimation.addEventListener('error', (error) => {
            });
            
          } catch (error) {
          }
        }
      } else {
        // Animation already exists, just play it
        this.lottieAnimation.play();
      }
    } else if (this.lottieAnimation) {
      // We're not on step 6, pause the animation
      this.lottieAnimation.pause();
    }
  }

  updateBallAnimation() {
    const ballContainer = document.getElementById('quiz-ball-animation');
    const catWrapper = document.getElementById('quiz-cat-wrapper');
    const catContainer = document.getElementById('quiz-cat-jumping-animation');
    
    if (!ballContainer || typeof lottie === 'undefined') return;
    
    // Determine if we're going forward or backward
    const isGoingBackward = this.previousStep && this.currentStep < this.previousStep;
    
    // Calculate positions
    const currentStepPercentage = ((this.currentStep - 1) / 6) * 100;
    const previousStepPercentage = this.previousStep ? ((this.previousStep - 1) / 6) * 100 : 0;
    
    // Set ball position exactly as before
    if (isGoingBackward) {
      // When going backward, keep the ball at the PREVIOUS position
      ballContainer.style.left = `${previousStepPercentage}%`;
    } else {
      // When going forward, move to new position
      ballContainer.style.transition = 'left 0.3s ease';
      ballContainer.style.left = `${currentStepPercentage}%`;
    }
    
    // Destroy existing ball animation if it exists
    if (this.ballAnimation) {
      this.ballAnimation.destroy();
      this.ballAnimation = null;
    }
    
    // Create new ball animation
    const ballAnimationPath = window.ballAnimationUrl || '/assets/Ball.json';
    
    try {
      // Configure ball animation
      this.ballAnimation = lottie.loadAnimation({
        container: ballContainer,
        renderer: 'svg',
        loop: false, // Play once
        autoplay: isGoingBackward ? false : true, // Auto-play for forward, manual for backward
        path: ballAnimationPath
      });
      
      // Speed up the ball animation slightly
      this.ballAnimation.setSpeed(1.5); // Play at 1.5x speed
      
      if (isGoingBackward) {
        // For reverse, immediately set direction and play from 80% through the animation
        this.ballAnimation.addEventListener('data_ready', () => {
          this.ballAnimation.setDirection(-1);
          const startFrame = Math.floor(this.ballAnimation.totalFrames * 0.8); // Start at 80% (20% from end)
          this.ballAnimation.goToAndPlay(startFrame, true);
        });
      } else {
      }
      
      
    } catch (error) {
    }
    
    // Handle cat animation with wrapper for consistent transitions
    if (catWrapper && catContainer) {
      // Position the wrapper (which has the CSS transition)
      // Add extra offset on last step (step 6)
      const extraOffset = this.currentStep === 6 ? 36 : 0;
      const totalOffset = 2 + extraOffset;
      
      if (isGoingBackward) {
        // For backward, cat also moves to the current position (one step left)
        catWrapper.style.left = `calc(${currentStepPercentage}% + ${totalOffset}px)`;
      } else {
        // For forward, wrapper moves to new position with offset
        catWrapper.style.left = `calc(${currentStepPercentage}% + ${totalOffset}px)`;
      }
      
      // Apply flip to the SVG element after animation loads
      const applyCatFlip = () => {
        const catSvg = catContainer.querySelector('svg');
        if (catSvg) {
          if (isGoingBackward) {
            catSvg.style.transform = 'scaleX(-1)';
          } else {
            catSvg.style.transform = 'scaleX(1)';
          }
        }
      };
      
      // Destroy and recreate cat animation
      if (this.catJumpingAnimation) {
        this.catJumpingAnimation.destroy();
        this.catJumpingAnimation = null;
      }
      
      const catAnimationPath = window.catJumpingAnimationUrl || '/assets/cat-jumping.json';
      
      try {
        this.catJumpingAnimation = lottie.loadAnimation({
          container: catContainer,
          renderer: 'svg',
          loop: false, // Play once
          autoplay: isGoingBackward ? false : true,
          path: catAnimationPath
        });
        
        if (isGoingBackward) {
          this.catJumpingAnimation.addEventListener('data_ready', () => {
            this.catJumpingAnimation.setDirection(-1);
            this.catJumpingAnimation.setSpeed(2); // Play at 2x speed (50% faster)
            const startFrame = Math.floor(this.catJumpingAnimation.totalFrames * 0.8);
            this.catJumpingAnimation.goToAndPlay(startFrame, true);
            applyCatFlip(); // Apply flip after animation is ready
            
            // Log animation duration
            const originalDuration = this.catJumpingAnimation.totalFrames / this.catJumpingAnimation.frameRate;
            const actualDuration = originalDuration / 2; // Divided by speed multiplier
          });
        } else {
          this.catJumpingAnimation.addEventListener('data_ready', () => {
            this.catJumpingAnimation.setSpeed(2); // Play at 2x speed (50% faster)
            applyCatFlip(); // Apply flip after animation is ready
            
            // Log animation duration
            const originalDuration = this.catJumpingAnimation.totalFrames / this.catJumpingAnimation.frameRate;
            const actualDuration = originalDuration / 2; // Divided by speed multiplier
          });
        }
      } catch (error) {
      }
    }
  }

  scrollToNewCat(catNumber) {
    
    // Find the new cat's section for the current step
    const newCatSection = document.querySelector(`.quiz-step[data-step="${this.currentStep}"][data-cat="${catNumber}"]`);
    if (!newCatSection) {
      return;
    }
    
    // Find the first input or the first h2 in the new cat section
    let targetElement = newCatSection.querySelector('h2') || newCatSection.querySelector('input');
    
    if (!targetElement) {
      return;
    }
    
    const scrollContainer = document.querySelector('.quiz-main-content');
    if (!scrollContainer) {
      return;
    }
    
    // Calculate the total offset to scroll
    let scrollPosition = 0;
    let currentElement = targetElement;
    
    while (currentElement && currentElement !== scrollContainer) {
      scrollPosition += currentElement.offsetTop;
      currentElement = currentElement.offsetParent;
    }
    
    console.log('[Auto-Scroll Debug] Scrolling to new cat section:', {
      catNumber: catNumber,
      targetElement: targetElement.tagName,
      scrollPosition: scrollPosition
    });
    
    // Smooth scroll to the new cat section
    const isMobile = window.innerWidth <= 768;
    const actualScrollContainer = isMobile ? document.querySelector('.quiz-content') : scrollContainer;
    
    if (actualScrollContainer) {
      actualScrollContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
    
    // Focus on the first input if it exists
    const firstInput = newCatSection.querySelector('input[type="text"], input[type="radio"]');
    if (firstInput && firstInput.type === 'text') {
      setTimeout(() => firstInput.focus(), 500); // Delay to allow scroll to complete
    }
  }

  updateVisualContent() {
    // Visual content now handled by CSS via :has() and data-current-step
    // No video elements to manage
  }

  updateProgressDisplay() {
    // Count visible steps for progress (accounts for skipped steps)
    const visibleSteps = [1, 2, 3, 4, 5, 6, 7].filter(s => !this.shouldSkipStep(s));
    const currentIndex = visibleSteps.indexOf(this.currentStep);
    const totalVisible = visibleSteps.length;

    // Update step indicator in top navigation
    const stepIndicator = document.querySelector('.quiz-step-indicator');
    if (stepIndicator) {
      stepIndicator.textContent = `${currentIndex + 1}/${totalVisible}`;
    }

    // Update progress bar in bottom navigation
    const progressBar = document.querySelector('.quiz-progress-bar');
    if (progressBar) {
      // Show 100% complete on step 6 (email/name collection)
      const percentage = this.currentStep === 6 ? 100 : ((currentIndex + 1) / totalVisible) * 100;
      progressBar.style.width = `${percentage}%`;
    }

    // Legacy support
    const progressText = document.querySelector('.quiz-progress-text');
    if (progressText) {
      progressText.textContent = `${currentIndex + 1} of ${totalVisible}`;
    }
  }

  completeQuiz() {
    if (window.QuizValidators.validateAllSteps(this.submissionData)) {
      
      // Calculate recommendations for all cats
      const allRecommendations = this.calculateAllCatsRecommendations();
      
      // Store recommendations
      window.QuizState.updateRecommendations(allRecommendations);
      window.QuizState.markQuizComplete();
      
      // Unlock body scroll
      window.QuizUtils.DOM.unlockBodyScroll();
      
      // Dispatch event for bundle system integration
      const event = new CustomEvent('quizCompleted', {
        detail: { 
          submissionData: this.submissionData,
          recommendations: allRecommendations
        }
      });
      document.dispatchEvent(event);
      
      // Show completion message
      this.showCompletionMessage(allRecommendations);
    }
  }

  calculateAllCatsRecommendations() {
    const catCount = this.submissionData.catCount || 1;
    const allRecommendations = {
      cats: {},
      totals: {
        bagsNeeded: 0,
        cansNeeded: 0,
        packsNeeded: 0
      }
    };

    // Calculate for each cat
    for (let i = 1; i <= catCount; i++) {
      const catData = this.submissionData[`cat${i}`];
      if (!catData) continue;

      const recommendations = window.QuizCalculator.calculate(catData);
      allRecommendations.cats[`cat${i}`] = recommendations;

      // Aggregate totals
      allRecommendations.totals.bagsNeeded += recommendations.bagsNeeded || 0;
      allRecommendations.totals.cansNeeded += recommendations.cansNeeded || 0;
      allRecommendations.totals.packsNeeded += recommendations.packsNeeded || 0;
    }

    return allRecommendations;
  }

  async clearQuizData() {
    // Show confirmation dialog
    const confirmed = await fuzzballConfirm('Are you sure you want to clear all quiz data and start over? This will remove all cat information and selections.', { title: 'Clear all quiz data?', confirmText: 'Clear' });

    if (!confirmed) {
      return;
    }
    
    // Clear all localStorage data
    const keysToRemove = [
      'submissionData',
      'quizRecommendations',
      'quiz_submitted',
      'klaviyo_submitted',
      'editMode',
      'bundleSelections',
      'trailBoxSelections',
      'trailGoal',
      'extraItems',
      'selectedExtras',
      'step10Selections',
      'quiz_pending_checkout'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      window.QuizState.clear(key);
    });
    
    // Reset instance data
    this.submissionData = {
      customer_name: '',
      customer_email: '',
      catCount: 1,
      cat1: {}
    };
    this.currentStep = 1;
    this.currentCat = 1;
    this.visitedSteps = new Set([1]); // Reset visited steps
    
    // Clear all form inputs
    const form = document.querySelector('.quiz-form');
    if (form) {
      form.reset();
    }
    
    // Clear any multi-cat sections
    document.querySelectorAll('.multi-cat-section').forEach(section => {
      if (section.dataset.cat !== '1') {
        section.remove();
      }
    });
    
    // Reset multi-cat counter
    const multiCatCounter = document.querySelector('.multi-cat-counter');
    if (multiCatCounter) {
      multiCatCounter.textContent = '1/3';
    }
    
    // Hide bundle and trail box systems if open
    const bundleSystem = document.querySelector('.bundle-system');
    if (bundleSystem) {
      bundleSystem.classList.remove('bundle-system-active');
    }
    
    const trailBoxSystem = document.querySelector('.trail-box-system');
    if (trailBoxSystem) {
      trailBoxSystem.classList.remove('trail-box-system-active');
    }
    
    // Clear bundle builder selections
    const bundleBuilder = document.querySelector('quiz-subscription-builder');
    if (bundleBuilder && bundleBuilder.clearSelections) {
      bundleBuilder.clearSelections();
    }
    
    // Clear trail box selections
    const trailBoxBuilder = document.querySelector('trail-box-builder');
    if (trailBoxBuilder && trailBoxBuilder.clearAllSelections) {
      trailBoxBuilder.clearAllSelections();
    }
    
    // Reset to step 1
    this.goToStep(1);
    
    // Update displays
    this.updateStepDisplay();
    this.updateProgressDisplay();
    this.updateVisualContent();
    
    // Show success message
    
    // Refresh the page to ensure clean state
    window.location.reload();
  }

  showCompletionMessage(recommendations) {

    // ── Quiz 2.1 Debug ──
    console.log('━━━ QUIZ 2.1 DEBUG ━━━');
    console.log(JSON.stringify({
      submissionData: this.submissionData,
      recommendations: recommendations
    }, null, 2));
    console.log('━━━ END DEBUG ━━━');

    const catCount = this.submissionData.catCount || 1;
    
    if (catCount === 1) {
      // Single cat message
      const catRec = recommendations.cats.cat1;
      const dietInfo = catRec.diet === 'Both' ? 
        `${catRec.bagsNeeded} bags dry food + ${catRec.packsNeeded} packs wet food` :
        catRec.diet === 'Wet' ? 
          `${catRec.packsNeeded} packs wet food` :
          `${catRec.bagsNeeded} bags dry food`;
      
      fuzzballAlert(`Monthly recommendation: ${dietInfo}`, { title: 'Quiz completed!' });
    } else {
      // Multiple cats message
      let message = `Quiz completed for ${catCount} cats!\n\n`;
      message += `Total monthly food: ${recommendations.totals.bagsNeeded} bags + ${recommendations.totals.packsNeeded} packs\n\n`;

      Object.entries(recommendations.cats).forEach(([catKey, catRec]) => {
        message += `${catRec.catName}\n`;
      });
      
      fuzzballAlert(message, { title: 'Quiz completed!' });
    }
  }

  // Edit mode handling
  handleEditMode(urlParams) {
    const quizId = urlParams.get('quiz_id');
    const editType = urlParams.get('edit_type');
    const catNumber = urlParams.get('cat');

    if (!quizId) {
      return;
    }

    // GA4: a user opened an existing box to edit it (deep-link from cart/plan
    // summary). Measures how many people re-enter to edit — Jacob's core question.
    quizGtagPush('event', 'quiz_edit_opened', {
      edit_type: editType || 'unknown',
      cat: catNumber || ''
    });
    
    
    // Try to load from localStorage first (set by cart page)
    const storedData = localStorage.getItem(`edit_${quizId}_data`);
    const storedProducts = localStorage.getItem(`edit_${quizId}_products`);
    
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        const products = storedProducts ? JSON.parse(storedProducts) : null;
        
        
        this.submissionData = data;
        window.QuizState.save('submissionData', data);
        
        // Store edit mode info with restrictions
        window.QuizState.set('editMode', {
          active: true,
          quizId: quizId,
          editType: editType,
          catNumber: catNumber,
          products: products,
          restricted: true  // Flag to indicate restricted edit mode
        });
        
        // Don't clear the temporary localStorage yet - keep it for refresh
        // It will be cleared when the user submits changes
        
        // Populate form with loaded data
        this.populateForm();
        
        // Go directly to step 7
        this.currentStep = 7;
        this.updateStepDisplay();
        this.updateProgressDisplay();
        
        // Calculate and display recommendations
        let dataForCalculation = this.submissionData;
        
        // If we're editing a specific cat in multi-cat mode, extract that cat's data
        if (catNumber && this.submissionData.catCount > 1 && this.submissionData[`cat${catNumber}`]) {
          dataForCalculation = this.submissionData[`cat${catNumber}`];
        } else if (this.submissionData.cat1 && !this.submissionData.fuzzballer_name) {
          // Multi-cat structure but no specific cat - calculate for all
          const recommendations = window.QuizCalculator.calculateMultiCat(this.submissionData);
          this.updateStep7Content(recommendations);
          this.lastCalculatedRecommendations = recommendations;
          // Skip the single-cat calculation below
          dataForCalculation = null;
        }
        
        let calculatedRecommendations = null;
        if (dataForCalculation) {
          calculatedRecommendations = window.QuizCalculator.calculate(dataForCalculation);
          this.updateStep7Content(calculatedRecommendations);
          this.lastCalculatedRecommendations = calculatedRecommendations;
        }
        
        // In edit mode, DON'T apply recommendations - the systems will restore from cart
        if (calculatedRecommendations && (!editType || editType === 'extras')) {
          // Only initialize with recommendations if not editing bundle/trail
          this.initializeBundleSystemWithRecommendations(calculatedRecommendations);
        } else if (editType === 'bundle' || editType === 'trail') {
        }
        
        // Open the appropriate system after a delay
        setTimeout(() => {
          
          // Hide tab navigation and other edit buttons in restricted edit mode
          const tabContainer = document.querySelector('.plan-tabs');
          if (tabContainer) {
            tabContainer.style.display = 'none';
          }
          
          // Hide all edit buttons since we're in restricted mode
          const allEditButtons = document.querySelectorAll('.edit-icon-wrapper, .trail-box-edit, .extras-edit, .edit-trail-box');
          allEditButtons.forEach(btn => {
            btn.style.display = 'none';
          });
          
          if (editType === 'bundle') {
            this.openBundleSystem(catNumber);
          } else if (editType === 'trail') {
            // Restore extras into QuizExtrasManager before opening the unified trail box modal.
            // The cart is the source of truth here: the manager may still hold extras
            // from a previous visit (step10Selections), so clear the edited cat first
            // and keep one entry per product, otherwise every edit duplicates them.
            if (products && products.extras && window.QuizExtrasManager) {
              window.QuizExtrasManager.selectedExtras[`cat${catNumber || '1'}`] = [];
              products.extras.forEach(extra => {
                const extraCatNumber = extra.properties?._cat_number || extra.cat_number || catNumber || '1';
                const catKey = `cat${extraCatNumber}`;

                if (!window.QuizExtrasManager.selectedExtras[catKey]) {
                  window.QuizExtrasManager.selectedExtras[catKey] = [];
                }
                if (window.QuizExtrasManager.selectedExtras[catKey].some(e => e.id === extra.id.toString())) {
                  return;
                }

                window.QuizExtrasManager.selectedExtras[catKey].push({
                  id: extra.id.toString(),
                  variantId: extra.variant_id || extra.variantId,
                  title: extra.title,
                  price: parseFloat(extra.price || 0),
                  quantity: extra.quantity || 1,
                  type: 'extra'
                });
              });
              window.QuizExtrasManager.save();
              window.QuizExtrasManager.updateUI();
            }
            this.openTrailBoxSystem(catNumber);
          } else {
          }
        }, 1000);
        
      } catch (error) {
        // Fall back to loading from cart
        this.loadFromCart(quizId, editType, catNumber);
      }
    } else {
      // Fall back to loading from cart
      this.loadFromCart(quizId, editType);
    }
  }
  
  loadFromCart(quizId, editType, catNumber = null) {
    window.QuizCartAPI.getQuizDataFromCart(quizId).then(data => {
      if (data) {
        this.submissionData = data;
        window.QuizState.save('submissionData', data);
        
        // Store edit mode info
        window.QuizState.set('editMode', {
          active: true,
          quizId: quizId,
          editType: editType,
          catNumber: catNumber
        });
        
        // Populate form with loaded data
        this.populateForm();
        
        // Go directly to step 7
        this.currentStep = 7;
        this.updateStepDisplay();
        this.updateProgressDisplay();
        
        // Calculate and display recommendations
        let dataForCalculation = this.submissionData;
        
        // If we're editing a specific cat in multi-cat mode, extract that cat's data
        if (catNumber && this.submissionData.catCount > 1 && this.submissionData[`cat${catNumber}`]) {
          dataForCalculation = this.submissionData[`cat${catNumber}`];
        } else if (this.submissionData.cat1 && !this.submissionData.fuzzballer_name) {
          // Multi-cat structure but no specific cat - calculate for all
          const recommendations = window.QuizCalculator.calculateMultiCat(this.submissionData);
          this.updateStep7Content(recommendations);
          this.lastCalculatedRecommendations = recommendations;
          // Skip the single-cat calculation below
          dataForCalculation = null;
        }
        
        let calculatedRecommendations = null;
        if (dataForCalculation) {
          calculatedRecommendations = window.QuizCalculator.calculate(dataForCalculation);
          this.updateStep7Content(calculatedRecommendations);
          this.lastCalculatedRecommendations = calculatedRecommendations;
        }
        
        // In edit mode, DON'T apply recommendations - the systems will restore from cart
        if (calculatedRecommendations && (!editType || editType === 'extras')) {
          // Only initialize with recommendations if not editing bundle/trail
          this.initializeBundleSystemWithRecommendations(calculatedRecommendations);
        } else if (editType === 'bundle' || editType === 'trail') {
        }
      } else {
        window.QuizUtils.Error.show('Could not load quiz data. Please start a new quiz.');
      }
    }).catch(error => {
      window.QuizUtils.Error.show('Error loading quiz data. Please try again.');
    });
  }

  // Extras popup handling
  showExtrasPopup(catNumber = null) {

    const extrasPopup = document.querySelector('.extras-popup-system');
    const overlay = document.querySelector('.quiz-modal-overlay');

    if (extrasPopup) {
      const catCount = this.submissionData.catCount || 1;
      const productsContainer = extrasPopup.querySelector('.extras-products-container');

      // Title comes from the Quiz Builder "Extras pop-up" settings; the first
      // "your" becomes the cat's name when there is one.
      const titleTemplate = extrasPopup.querySelector('.extras-popup-subtitle')?.dataset.title
        ?? 'Get some extras for your Trial box';
      const titleFor = (name) => (name ? titleTemplate.replace(/\byour\b/i, `${name}'s`) : titleTemplate);

      if (catCount > 1 && productsContainer) {
        // Multi-cat: build stacked per-cat sections
        // Store original grid as template if not already stored
        if (!extrasPopup._originalGrid) {
          extrasPopup._originalGrid = productsContainer.innerHTML;
        }

        // Hide the shared subtitle (each section gets its own header)
        const subtitle = extrasPopup.querySelector('.extras-popup-subtitle');
        if (subtitle) subtitle.style.display = 'none';

        // Build per-cat sections
        productsContainer.innerHTML = '';
        for (let i = 1; i <= catCount; i++) {
          const catData = this.submissionData[`cat${i}`];
          const catName = catData?.fuzzballer_name || `Cat ${i}`;

          const section = document.createElement('div');
          section.className = 'extras-cat-section';
          section.setAttribute('data-cat', i);

          section.innerHTML = `
            <div class="extras-cat-section-header">
              <p class="Md_Body_Bold"></p>
            </div>
            ${extrasPopup._originalGrid}
          `;
          section.querySelector('.extras-cat-section-header p').textContent = titleFor(catName);
          productsContainer.appendChild(section);
        }

        // Remove popup-level data-editing-cat — each section carries its own
        extrasPopup.removeAttribute('data-editing-cat');

        // Re-bind listeners for the cloned buttons
        extrasPopup.dataset.listenersSetup = 'false';
      } else {
        // Single cat: restore original grid if it was replaced
        if (extrasPopup._originalGrid) {
          const productsContainer = extrasPopup.querySelector('.extras-products-container');
          if (productsContainer) productsContainer.innerHTML = extrasPopup._originalGrid;
          extrasPopup._originalGrid = null;
          extrasPopup.dataset.listenersSetup = 'false';
        }

        // Show the shared subtitle
        const subtitle = extrasPopup.querySelector('.extras-popup-subtitle');
        if (subtitle) subtitle.style.display = '';

        if (catNumber) {
          extrasPopup.setAttribute('data-editing-cat', catNumber);
          const catData = this.submissionData[`cat${catNumber}`];
          const headerTitle = extrasPopup.querySelector('.extras-popup-subtitle .Md_Body_Bold');
          if (headerTitle && catData) {
            headerTitle.textContent = titleFor(catData.fuzzballer_name || `Cat ${catNumber}`);
          }
        } else {
          extrasPopup.removeAttribute('data-editing-cat');
          const headerTitle = extrasPopup.querySelector('.extras-popup-subtitle .Md_Body_Bold');
          if (headerTitle) {
            headerTitle.textContent = titleTemplate;
          }
        }
      }

      extrasPopup.style.display = 'block';

      // Trigger animation after display change
      setTimeout(() => {
        extrasPopup.classList.add('extras-popup-active');
      }, 10);

      // Show overlay
      if (overlay) overlay.classList.add('active');

      // Prevent body scrolling
      document.body.classList.add('overflow-hidden');

      // Setup event listeners for extras popup
      this.setupExtrasPopupListeners();

      // Update buttons to check inventory and show current cat's selections
      if (window.QuizExtrasManager) {
        window.QuizExtrasManager.updateExtrasPopupButtons();
      }
    }
  }
  
  closeExtrasPopup() {

    const extrasPopup = document.querySelector('.extras-popup-system');
    const overlay = document.querySelector('.quiz-modal-overlay');

    // Mark popup as shown (works for both single and multi-cat)
    this.extrasPopupShown = true;

    // Normal close behavior
    if (extrasPopup) {
      extrasPopup.classList.remove('extras-popup-active');

      // Hide after animation
      setTimeout(() => {
        extrasPopup.style.display = 'none';
      }, 400);
    }

    // Hide overlay
    if (overlay) overlay.classList.remove('active');

    // Re-enable body scrolling
    document.body.classList.remove('overflow-hidden');
  }
  
  async handleExtrasReturnToCart() {
    console.log('🛑 RETURN TO CART: Called');
    
    // Get edit mode info
    const editMode = window.QuizState.get('editMode');
    if (!editMode || !editMode.quizId) {
      console.log('❌ No edit mode found');
      return;
    }
    
    console.log('📋 Edit mode info:', editMode);
    
    // In edit mode, we don't do sequential popups - just close and return
    // The sequential popup logic is only for Step 5 flow
    
    try {
      // Show loading state
      const nextBtn = document.querySelector('.extras-next-btn');
      if (nextBtn) {
        nextBtn.disabled = true;
        nextBtn.textContent = 'Updating cart...';
      }
      
      // Get current extras selections for the specific cat
      const extrasItems = window.QuizExtrasManager ? window.QuizExtrasManager.getExtras(editMode.catNumber) : [];
      
      // Remove old extras items for this cat
      await window.QuizCartAPI.removeCartItemsByQuizId(editMode.quizId, 'extras', editMode.catNumber);
      
      // Build quiz cart items using the API function (will apply condensed format)
      let cartItems = [];
      if (extrasItems.length > 0) {
        // Get submission data
        const submissionData = window.QuizState.get('submissionData') || {};
        
        // Parameters: submissionData, bundleItems, trailItems, extrasItems, isEditMode, editQuizId
        cartItems = await window.QuizCartAPI.buildQuizCartItems(
          submissionData,  // submission data
          [],              // bundle items (empty for extras-only edit)
          [],              // trail items (empty for extras-only edit)
          extrasItems,     // extras items
          true,            // isEditMode
          editMode.quizId  // editQuizId
        );
      }
      
      // Add new items to cart if any
      if (cartItems.length > 0) {
        await window.QuizCartAPI.addItems(cartItems);
      }
      
      // Clear edit mode data
      localStorage.removeItem(`edit_${editMode.quizId}_data`);
      localStorage.removeItem(`edit_${editMode.quizId}_products`);
      
      // Redirect to cart
      window.location.href = '/cart';
      
    } catch (error) {
      window.QuizUtils.Error.show('Error updating cart. Please try again.');
      
      // Re-enable button
      const nextBtn = document.querySelector('.extras-next-btn');
      if (nextBtn) {
        nextBtn.disabled = false;
        nextBtn.textContent = 'Return to Cart';
      }
    }
  }

  setupExtrasPopupListeners() {
    const extrasPopup = document.querySelector('.extras-popup-system');
    if (!extrasPopup) return;
    
    // Check if listeners already set up to avoid duplicates
    if (extrasPopup.dataset.listenersSetup === 'true') {
      return;
    }
    extrasPopup.dataset.listenersSetup = 'true';
    
    // Check if in restricted edit mode
    const editMode = window.QuizState.get('editMode');
    if (editMode?.active && editMode?.restricted) {
      // Hide close button
      const closeBtn = extrasPopup.querySelector('.extras-popup-close');
      if (closeBtn) {
        closeBtn.style.display = 'none';
      }
      
      // Replace next button with return to cart button
      const nextBtn = extrasPopup.querySelector('.extras-next-btn');
      if (nextBtn) {
        nextBtn.textContent = 'Return to Cart';
        nextBtn.removeEventListener('click', this.closeExtrasPopup);
        nextBtn.addEventListener('click', () => this.handleExtrasReturnToCart());
      }
    } else {
      // Normal mode - close button
      const closeBtn = extrasPopup.querySelector('.extras-popup-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeExtrasPopup());
      }
      
      // Next button
      const nextBtn = extrasPopup.querySelector('.extras-next-btn');
      if (nextBtn) {
        nextBtn.addEventListener('click', () => this.closeExtrasPopup());
      }
    }
    
    // Add/Remove buttons for extras
    const addButtons = extrasPopup.querySelectorAll('.extras-add-btn');
    addButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const card = e.currentTarget.closest('.extras-product-card');
        const productId = card?.dataset.productId;
        const variantId = card?.dataset.variantId;
        const title = card?.querySelector('.product-title')?.textContent;
        const priceEl = card?.querySelector('.product-sale-price');
        const price = priceEl ? parseFloat(priceEl.textContent.replace('£', '')) : 0;
        const inventoryQuantity = parseInt(card?.dataset.inventoryQuantity || '0');

        if (productId && window.QuizExtrasManager) {
          // Get cat from closest per-cat section (multi-cat) or popup attribute (single cat)
          const catSection = card.closest('.extras-cat-section');
          const editingCat = catSection
            ? parseInt(catSection.getAttribute('data-cat')) || 1
            : parseInt(extrasPopup.getAttribute('data-editing-cat')) || 1;

          // Use extras manager to handle add/remove
          const wasAdded = window.QuizExtrasManager.toggleExtra({
            id: productId,
            variantId: variantId,
            inventoryQuantity: inventoryQuantity,
            title: title,
            price: price
          }, editingCat);

          // Update button state
          if (wasAdded) {
            btn.innerHTML = 'Remove';
            btn.classList.add('added');
          } else {
            btn.innerHTML = 'Add<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 9H3L2 21H10M14 11V7C14 4.79086 12.2091 3 10 3C7.79086 3 6 4.79086 6 7V11" stroke="#434548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M17 13V23M22 18L12 18" stroke="#434548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            btn.classList.remove('added');
          }
        }
      });
    });

    // Update button states when popup opens
    if (window.QuizExtrasManager) {
      window.QuizExtrasManager.updateExtrasPopupButtons();
    }
  }

  setupTrailBoxExtrasListeners() {
    const trailBoxSystem = document.querySelector('.trail-box-system');
    const extrasSection = trailBoxSystem?.querySelector('.trail-box-extras-section');
    if (!extrasSection) return;

    // Guard against duplicate setup
    if (extrasSection.dataset.listenersSetup === 'true') return;
    extrasSection.dataset.listenersSetup = 'true';

    const addButtons = extrasSection.querySelectorAll('.extras-add-btn');
    addButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const card = e.currentTarget.closest('.extras-product-card');
        const productId = card?.dataset.productId;
        const variantId = card?.dataset.variantId;
        const title = card?.querySelector('.product-title')?.textContent;
        const priceEl = card?.querySelector('.product-sale-price');
        const price = priceEl ? parseFloat(priceEl.textContent.replace('£', '')) : 0;
        const inventoryQuantity = parseInt(card?.dataset.inventoryQuantity || '0');

        if (productId && window.QuizExtrasManager) {
          const editingCat = parseInt(trailBoxSystem.getAttribute('data-editing-cat')) || 1;

          const wasAdded = window.QuizExtrasManager.toggleExtra({
            id: productId,
            variantId: variantId,
            inventoryQuantity: inventoryQuantity,
            title: title,
            price: price
          }, editingCat);

          if (wasAdded) {
            btn.innerHTML = 'Remove';
            btn.classList.add('added');
          } else {
            btn.innerHTML = 'Add<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 9H3L2 21H10M14 11V7C14 4.79086 12.2091 3 10 3C7.79086 3 6 4.79086 6 7V11" stroke="#434548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M17 13V23M22 18L12 18" stroke="#434548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            btn.classList.remove('added');
          }
        }
      });
    });
  }

  // Checkout handling
  async handleCheckout(e) {
    e.preventDefault();

    // First-delivery-date gate. Show the modal BEFORE any cart mutation
    // or state write so dismissing/refreshing leaves the page in its
    // original state — no items added, no localStorage flags set, no
    // partial work to undo. When the feature is disabled, the modal
    // module resolves true immediately so this is a no-op.
    if (window.FUZZBALL_FIRST_DELIVERY_ENABLED && window.FuzzballFirstDelivery) {
      const confirmed = await window.FuzzballFirstDelivery.askForDate();
      if (!confirmed) return;
    }

    console.log('🚀 CHECKOUT HANDLER CALLED');

    // Check if we're in edit mode
    const editModeCheck = window.QuizState.get('editMode');
    console.log('📝 Edit mode status:', editModeCheck);
    
    // Check what's already in the cart before adding
    try {
      const existingCart = await window.QuizCartAPI.getCart();
      if (existingCart.items.length > 0) {
        const cartSummary = {};
        existingCart.items.forEach(item => {
          const key = `${item.variant_id} - ${item.product_title}`;
          if (!cartSummary[key]) {
            cartSummary[key] = { 
              quantity: 0, 
              hasSubscription: false,
              properties: []
            };
          }
          cartSummary[key].quantity += item.quantity;
          if (item.selling_plan) {
            cartSummary[key].hasSubscription = true;
          }
          if (item.properties && Object.keys(item.properties).length > 0) {
            cartSummary[key].properties.push(item.properties);
          }
        });
        
        // Check specifically for Beef products
        const beefItems = existingCart.items.filter(item => 
          item.product_title.toLowerCase().includes('beef') || 
          item.variant_title.toLowerCase().includes('beef')
        );
        if (beefItems.length > 0) {
          console.log('  ⚠️ Found Beef products in cart:', beefItems.map(item => ({
            title: item.product_title,
            variant: item.variant_title,
            variant_id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties
          })));
        }
        
        // Check for the specific variant ID that's causing issues
        const problematicVariantId = '55398955090295'; // Beef - Single Can
        const problematicItems = existingCart.items.filter(item => 
          item.variant_id === problematicVariantId
        );
        if (problematicItems.length > 0) {
          console.log(`  ⚠️ Found items with variant ID ${problematicVariantId}:`, problematicItems.map(item => ({
            title: item.product_title,
            quantity: item.quantity,
            source: item.properties?._source,
            cat_number: item.properties?._cat_number,
            cat_name: item.properties?._cat_name
          })));
        }
      }
    } catch (error) {
    }
    
    const checkoutBtn = e.target.closest('.quiz-checkout-btn');
    const originalText = checkoutBtn.textContent;
    checkoutBtn.textContent = 'Processing...';
    checkoutBtn.disabled = true;
    
    try {
      // Gather all items for checkout
      const bundleElement = document.querySelector('quiz-bundle');
      const trailBoxElement = document.querySelector('trail-box');
      
      // Check if in edit mode
      const editMode = window.QuizState.get('editMode');
      const isEditMode = editMode?.active;
      const editQuizId = editMode?.quizId;
      
      // Get bundle selections - pass true to get all cats' selections
      if (!bundleElement || typeof bundleElement.getSelections !== 'function') {
        throw new Error('Bundle system is not ready. Please wait a moment and try again.');
      }
      
      // IMPORTANT: Always get all cats' selections when in edit mode or multi-cat
      const forceAllCats = isEditMode || this.submissionData.catCount > 1;
      
      // Get basic selections first
      const basicBundleItems = bundleElement.getSelections(forceAllCats) || [];
      
      console.log('🔍 CHECKOUT: Basic bundle items before Recharge:', basicBundleItems);
      
      // Convert to Recharge bundle items with proper _rc_bundle properties
      let bundleItems;
      if (bundleElement.getRechargeBundleItems) {
        console.log('🔵 CHECKOUT: Calling getRechargeBundleItems to add Recharge properties...');
        bundleItems = await bundleElement.getRechargeBundleItems(basicBundleItems);
        console.log('✅ CHECKOUT: Bundle items after Recharge processing:', bundleItems);
        
        // Verify each item has _rc_bundle properties
        bundleItems.forEach((item, index) => {
          if (item.properties && item.properties._rc_bundle) {
            console.log(`✅ CHECKOUT: Item ${index + 1} has _rc_bundle:`, item.properties._rc_bundle);
          } else {
            console.warn(`⚠️ CHECKOUT: Item ${index + 1} missing _rc_bundle properties!`, item);
          }
        });
      } else {
        console.warn('⚠️ CHECKOUT: getRechargeBundleItems method not found, using basic items');
        bundleItems = basicBundleItems;
      }
      
      console.log('Bundle items by cat:', bundleItems.reduce((acc, item) => {
        const catNum = item.cat_number || '1';
        acc[catNum] = (acc[catNum] || 0) + 1;
        return acc;
      }, {}));
      
      if (bundleItems.length === 0) {
        throw new Error('Please select items for your monthly subscription');
      }
      
      // Get trail box selections - pass true to get all cats' selections
      let trailItems = trailBoxElement ? trailBoxElement.getSelections(forceAllCats) || [] : [];
      console.log('Trail items by cat:', trailItems.reduce((acc, item) => {
        const catNum = item.cat_number || '1';
        acc[catNum] = (acc[catNum] || 0) + 1;
        return acc;
      }, {}));
      
      // DEBUG: Log detailed trail items
      if (trailBoxElement?.selections) {
        Object.entries(trailBoxElement.selections).forEach(([catKey, catSelections]) => {
          if (catSelections && catSelections.length > 0) {
            catSelections.forEach((item, idx) => {
            });
          }
        });
      }
      
      // Log what we expect vs what we got
      trailItems.forEach((item, idx) => {
      });
      
      // In edit mode, if trail box hasn't been opened, use stored trail products
      if (isEditMode && trailItems.length === 0 && editMode?.products?.trail) {
        trailItems = editMode.products.trail.map(p => ({
          id: p.id,
          variant_id: p.variant_id || p.variantId,  // Use snake_case to match cart API
          quantity: p.quantity,
          title: p.title,
          type: p.type || 'can'
        }));
      }
      
      // Get extras from extras manager - now returns object with cat keys
      let extrasItems = window.QuizExtrasManager ? window.QuizExtrasManager.getExtras() : {};
      
      // In edit mode, only restore extras if the popup hasn't been shown
      // Check if extras popup was shown to avoid restoring when user intentionally removed all
      const extrasPopupShown = this.extrasPopupShown || (editMode?.editType === 'extras');
      if (isEditMode && extrasItems.length === 0 && editMode?.products?.extras && !extrasPopupShown) {
        // Clear existing extras first
        window.QuizExtrasManager.clear();
        // Restore extras to the manager with price validation
        editMode.products.extras.forEach(extra => {
          const extraCatNumber = extra.properties?._cat_number || extra.cat_number || '1';
          const catKey = `cat${extraCatNumber}`;
          
          // Ensure the cat array exists
          if (!window.QuizExtrasManager.selectedExtras[catKey]) {
            window.QuizExtrasManager.selectedExtras[catKey] = [];
          }
          
          // Ensure price is a number
          const validExtra = {
            ...extra,
            price: parseFloat(extra.price) || 0
          };
          window.QuizExtrasManager.selectedExtras[catKey].push(validExtra);  
        });
        window.QuizExtrasManager.save();
        window.QuizExtrasManager.updateUI();
        extrasItems = window.QuizExtrasManager.getExtrasForCurrentContext();
      }
      
      // If in edit mode, only remove items for the specific edit type
      if (isEditMode && editQuizId && editMode?.editType) {
        const editingCatNumber = editMode.catNumber;
        
        console.log('🔍 EDIT MODE DEBUG:', {
          editType: editMode.editType,
          editingCatNumber,
          bundleItemsCount: bundleItems.length,
          hasBundleElement: !!bundleElement,
          hasGetRechargeBundleItems: !!(bundleElement && bundleElement.getRechargeBundleItems)
        });
        
        if (editMode.editType === 'extras' && editingCatNumber) {
          // For extras, only remove that specific cat's extras
          await this.removeOldCartItemsForCat(editQuizId, 'extras', editingCatNumber);
        } else if (editMode.editType === 'trail' && editingCatNumber) {
          // For trail, only remove that specific cat's trail items
          await this.removeOldCartItemsForCat(editQuizId, 'trail', editingCatNumber);
        } else if (editMode.editType === 'bundle') {
          console.log('🔵 EDIT MODE: Bundle edit detected');
          // For bundle, only remove the specific cat's bundle items (not all cats)
          if (editingCatNumber) {
            await this.removeOldCartItemsForCat(editQuizId, 'bundle', editingCatNumber);
          } else {
            await this.removeOldCartItems(editQuizId, 'bundle');
          }
          
          // IMPORTANT: Bundle items need to go through Recharge process even in edit mode
          console.log('🔵 EDIT MODE: About to process bundle items through Recharge...', {
            bundleItemsBeforeRecharge: bundleItems
          });
          
          if (bundleElement && bundleElement.getRechargeBundleItems && bundleItems.length > 0) {
            console.log('🔵 EDIT MODE: Calling getRechargeBundleItems...');
            bundleItems = await bundleElement.getRechargeBundleItems(bundleItems);
            console.log('✅ EDIT MODE: Bundle items after Recharge:', bundleItems);
            
            // Verify Recharge properties
            bundleItems.forEach((item, idx) => {
              console.log(`Item ${idx + 1} Recharge properties:`, {
                has_rc_bundle: !!(item.properties && item.properties._rc_bundle),
                _rc_bundle: item.properties?._rc_bundle || 'MISSING',
                _rc_bundle_variant: item.properties?._rc_bundle_variant || 'MISSING'
              });
            });
          } else {
            console.error('❌ EDIT MODE: Cannot process Recharge bundle!', {
              hasBundleElement: !!bundleElement,
              hasMethod: !!(bundleElement && bundleElement.getRechargeBundleItems),
              bundleItemsLength: bundleItems.length
            });
          }
        }
      } else if (!isEditMode && bundleItems.length > 0) {
        // Regular checkout (non-edit mode) - bundle items already processed through Recharge above
        console.log('✅ Regular checkout: Bundle items already have Recharge properties');
      }
      
      // Build cart items
      const cartItems = window.QuizCartAPI.buildQuizCartItems(
        this.submissionData,
        bundleItems,
        trailItems,
        extrasItems,
        isEditMode,
        editQuizId
      );
      
      
      // Add all items to cart in a single batch
      
      // Prepare all items for the cart
      const allItemsToAdd = cartItems.map(item => ({
        id: item.id,
        quantity: item.quantity,
        selling_plan: item.selling_plan,
        properties: item.properties
      }));
      
      
      // Log breakdown by cat and type
      const itemsByCat = {};
      cartItems.forEach(item => {
        const catNumber = item.properties._cat_number || '1';
        const catName = item.properties._cat_name || `Cat ${catNumber}`;
        const source = item.properties._source || 'unknown';
        
        if (!itemsByCat[catNumber]) {
          itemsByCat[catNumber] = { name: catName, bundle: 0, trail: 0, extras: 0 };
        }
        itemsByCat[catNumber][source]++;
      });
      
      Object.entries(itemsByCat).forEach(([catNumber, counts]) => {
      });
      
      try {
        // Add all items in one request
        await window.QuizCartAPI.addItems(allItemsToAdd);
      } catch (error) {
        
        // If batch fails, try per-cat batches as fallback
        
        // Group items by cat for fallback
        const itemsByCatDetailed = {};
        cartItems.forEach(item => {
          const catNumber = item.properties._cat_number || '1';
          if (!itemsByCatDetailed[catNumber]) {
            itemsByCatDetailed[catNumber] = [];
          }
          itemsByCatDetailed[catNumber].push({
            id: item.id,
            quantity: item.quantity,
            selling_plan: item.selling_plan,
            properties: item.properties
          });
        });
        
        // Try each cat separately
        for (const [catNumber, catItems] of Object.entries(itemsByCatDetailed)) {
          const catName = catItems[0]?.properties._cat_name || `Cat ${catNumber}`;
          
          try {
            await window.QuizCartAPI.addItems(catItems);
          } catch (catError) {
            
            // If cat batch fails, try individual items
            let successCount = 0;
            let failedItems = [];
            
            for (let i = 0; i < catItems.length; i++) {
              const item = catItems[i];
              try {
                await window.QuizCartAPI.addItems([item]);
                successCount++;
              } catch (itemError) {
                failedItems.push({
                  item: item,
                  error: itemError.message
                });
              }
            }
            
            if (failedItems.length > 0) {
            }
          }
        }
      }
      
      // Store checkout data
      window.QuizState.set('checkoutData', {
        bundleItems,
        trailItems,
        extrasItems,
        totalPrice: this.calculateTotalPrice(bundleItems, trailItems, extrasItems)
      });
      
      // Show success message
      window.QuizUtils.Success.show('Items added to cart! Redirecting...');
      
      // Clear edit mode state if we were editing
      if (isEditMode) {
        window.QuizState.clear('editMode');
        // Clear temporary edit data now that changes are submitted
        if (editQuizId) {
          localStorage.removeItem(`edit_${editQuizId}_data`);
          localStorage.removeItem(`edit_${editQuizId}_products`);
        }
      }
      
      // Update button and redirect to checkout
      checkoutBtn.textContent = 'Redirecting to checkout...';
      checkoutBtn.disabled = false;

      // Set flag to detect if user returns via browser back button
      // This prevents duplicate bundle creation if they back out of checkout
      const checkoutTimestamp = Date.now().toString();
      console.log('[Quiz Debug] Setting quiz_pending_checkout flag:', checkoutTimestamp);
      localStorage.setItem('quiz_pending_checkout', checkoutTimestamp);

      // Verify discount carrier is in cart before redirecting to checkout
      setTimeout(async () => {
        if (window.DiscountCarrierGuard) {
          const ok = await window.DiscountCarrierGuard.ensureDiscountCarrier();
          if (!ok) {
            checkoutBtn.textContent = 'Error - please try again';
            checkoutBtn.disabled = false;
            window.QuizUtils.Success.show('Something went wrong preparing your order. Please try again.');
            localStorage.removeItem('quiz_pending_checkout');
            return;
          }
        }
        window.location.href = '/checkout';
      }, 1500);
      
      // Compare what we tried to add vs what's in the cart
      setTimeout(async () => {
        const finalCart = await window.QuizCartAPI.getCart();
        
        // Group items by quiz and source
        const cartAnalysis = {};
        finalCart.items.forEach(item => {
          const quizId = item.properties?._quiz_id || 'no_quiz';
          const source = item.properties?._source || 'no_source';
          const catNumber = item.properties?._cat_number || 'no_cat';
          const key = `${quizId}_${source}_cat${catNumber}`;
          
          if (!cartAnalysis[key]) {
            cartAnalysis[key] = {
              quizId,
              source,
              catNumber,
              catName: item.properties?._cat_name || 'Unknown',
              items: []
            };
          }
          
          cartAnalysis[key].items.push({
            title: item.product_title,
            quantity: item.quantity,
            variant_id: item.variant_id,
            product_id: item.product_id
          });
        });
        
        
        // Show only the current quiz items
        const currentQuizId = isEditMode ? editQuizId : `quiz_${Date.now()}`.substring(0, 20);
        Object.values(cartAnalysis).forEach(group => {
          if (group.quizId.startsWith(currentQuizId.substring(0, 15))) {
            group.items.forEach(item => {
            });
          }
        });
      }, 2000);
      
      // UNCOMMENT TO RE-ENABLE REDIRECT:
      // setTimeout(() => {
      //   window.location.href = '/cart';
      // }, 1500);
      
    } catch (error) {
      window.QuizUtils.Error.show(error.message || 'Error during checkout. Please try again.');
      
      // Reset button
      checkoutBtn.textContent = originalText;
      checkoutBtn.disabled = false;
    }
  }

  mapExtrasToProducts(extras) {
    // This would map extras selections to actual product variant IDs
    // For now, return empty array as placeholder
    const extrasMapping = {
      'litter': { variantId: '12345', price: 12.99 },
      'treats': { variantId: '12346', price: 8.99 },
      'toys': { variantId: '12347', price: 5.99 }
    };
    
    return extras.map(extra => extrasMapping[extra]).filter(Boolean);
  }
  
  async removeOldCartItems(quizId, source) {
    // Delegate to QuizCartAPI which handles both _qdata and old format
    await window.QuizCartAPI.removeCartItemsByQuizId(quizId, source);
  }

  async removeOldCartItemsForCat(quizId, source, catNumber) {
    // Delegate to QuizCartAPI which handles both _qdata and old format
    await window.QuizCartAPI.removeCartItemsByQuizId(quizId, source, catNumber);
  }

  calculateTotalPrice(bundleItems, trailItems, extrasItems) {
    let total = 0;
    
    // Bundle items
    bundleItems.forEach(item => {
      total += item.price * item.quantity;
    });
    
    // Trail items (usually one-time purchase)
    trailItems.forEach(item => {
      total += (item.price || 0) * (item.quantity || 1);
    });
    
    // Extras - handle both array and object formats
    if (Array.isArray(extrasItems)) {
      // Legacy format - single array
      extrasItems.forEach(item => {
        total += item.price || 0;
      });
    } else if (typeof extrasItems === 'object') {
      // New format - object with cat keys
      Object.values(extrasItems).forEach(catExtras => {
        if (Array.isArray(catExtras)) {
          catExtras.forEach(item => {
            total += item.price || 0;
          });
        }
      });
    }
    
    return total;
  }

  // Helper method to handle quiz restart
  resetQuiz() {
    
    // Clear all stored data
    window.QuizState.clear();
    
    // Reset instance variables
    this.currentStep = 1;
    this.visitedSteps = new Set([1]); // Reset visited steps
    this.submissionData = {
      catCount: 1,
      cat1: {
        fuzzballer_name: '',
        breed: '',
        age_range: '',
        wet_dry_preference: '',
        cans_per_day: '',
        health_concerns_exists: '',
        health_concerns: '',
        lifestyle: '',
        disliked_flavors: []
      }
    };
    this.extrasPopupShown = false;

    // Clear form fields
    const form = document.querySelector('.quiz-form');
    if (form) {
      form.reset();
    }
    
    // Update display
    this.updateStepDisplay();
    this.updateProgressDisplay();
    
    // Remove any URL parameters
    window.QuizUtils.Browser.updateUrl({
      mode: null,
      quiz_id: null,
      checkout: null
    });
    
  }

  // Utility method to check if quiz is complete
  isQuizComplete() {
    return window.QuizValidators.validateAllSteps(this.submissionData);
  }

  // Get current quiz data for external use
  getQuizData() {
    return {
      submissionData: this.submissionData,
      currentStep: this.currentStep,
      isComplete: this.isQuizComplete(),
      recommendations: window.QuizState.get('recommendations')
    };
  }

  // Multi-cat functionality
  setupAddCatButton() {
    const addCatBtn = document.querySelector('.add-another-cat');
    if (addCatBtn) {
      // Remove existing listeners to avoid duplicates
      const newBtn = addCatBtn.cloneNode(true);
      addCatBtn.parentNode.replaceChild(newBtn, addCatBtn);
      
      // Add click listener
      newBtn.addEventListener('click', () => this.addNewCat());
    }
  }

  addNewCat() {
    const catCount = this.submissionData.catCount || 1;
    if (catCount >= 3) {
      fuzzballAlert('Maximum 3 cats allowed');
      return;
    }

    // Add new cat to state
    const newCatNumber = window.QuizState.addCat();
    if (!newCatNumber) return;

    // Clone quiz steps for the new cat
    this.duplicateStepsForCat(newCatNumber);

    // Update button visibility
    if (newCatNumber >= 3) {
      document.querySelector('.add-another-cat').style.display = 'none';
    }

    
    // Small delay to ensure DOM is ready before validating
    setTimeout(() => {
      // Initialize breed handler only for the new cat
      this.setupBreedUnknownHandlerForCat(newCatNumber);
      
      // Revalidate current step to update navigation buttons
      // This will disable the next button since the new cat has no data yet
      const isValid = window.QuizValidators.validateStep(this.currentStep, this.submissionData);
      this.updateNavigationButtons(isValid);
      
      // Auto-scroll to the first unanswered question (which will be in the new cat)
      this.scrollToFirstUnansweredQuestion();
    }, 100);
  }

  duplicateStepsForCat(catNumber) {
    // For steps 1-5, clone the HTML structure
    for (let step = 1; step <= 5; step++) {
      const originalStep = document.querySelector(`[data-step="${step}"][data-cat="1"]`);
      if (!originalStep) continue;

      const clonedStep = originalStep.cloneNode(true);
      
      // Update attributes
      clonedStep.setAttribute('data-cat', catNumber);
      
      // Special handling for step 4 dislikes section - preserve all styles including background images
      if (step === 4) {
        // Get ALL option-image elements, not just ones with background-image
        const originalDislikes = originalStep.querySelectorAll('.dislikes-section .option-image');
        const clonedDislikes = clonedStep.querySelectorAll('.dislikes-section .option-image');
        
        // Copy all inline styles from original to cloned elements
        originalDislikes.forEach((originalImg, index) => {
          if (clonedDislikes[index]) {
            // Get the label text for debugging
            const label = originalImg.parentElement.querySelector('.checkbox-label');
            const flavorName = label ? label.textContent.trim() : 'Unknown';
            
            // Copy the entire style attribute to preserve all inline styles
            if (originalImg.hasAttribute('style')) {
              const styleValue = originalImg.getAttribute('style');
              clonedDislikes[index].setAttribute('style', styleValue);
            } else {
            }
          }
        });
      }
      
      // Cat name already shown via .cat-name-display spans — no prefix needed
      
      // Add delete button only for step 1
      if (step === 1) {
        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'delete-cat-btn';
        deleteBtn.type = 'button';
        deleteBtn.setAttribute('data-cat', catNumber);
        deleteBtn.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        deleteBtn.addEventListener('click', () => this.confirmCatDeletion(catNumber));
        
        // Add to the step container
        clonedStep.style.position = 'relative';
        clonedStep.appendChild(deleteBtn);
      }

      // Update form field names and IDs
      this.updateFieldNamesForCat(clonedStep, catNumber);

      // Insert after the previous cat's step
      const previousCatStep = document.querySelector(`[data-step="${step}"][data-cat="${catNumber-1}"]`);
      if (previousCatStep && previousCatStep.parentNode) {
        previousCatStep.parentNode.insertBefore(clonedStep, previousCatStep.nextSibling);
      }
    }
  }

  updateFieldNamesForCat(stepElement, catNumber) {
    // Update all input names to be cat-specific
    stepElement.querySelectorAll('input, select, textarea').forEach(field => {
      const originalName = field.name;
      
      // Skip renaming breed-unknown checkbox as it's just a UI helper
      if (originalName !== 'breed-unknown') {
        // Update the name attribute to include cat number to avoid conflicts
        field.name = `cat${catNumber}_${originalName}`;
      }
      
      // Update IDs to be unique for each cat
      if (field.id === 'breed-unknown') {
        field.id = `breed-unknown-cat${catNumber}`;
        // Also update the corresponding label
        const label = stepElement.querySelector(`label[for="breed-unknown"]`);
        if (label) {
          label.setAttribute('for', `breed-unknown-cat${catNumber}`);
        }
      } else if (field.id === 'age-dropdown') {
        field.id = `age-dropdown-cat${catNumber}`;
      }
      
      // Clear values
      if (field.type === 'radio' || field.type === 'checkbox') {
        field.checked = false;
      } else {
        field.value = '';
      }
      
      // Clear visual state classes
      field.classList.remove('input-filled');
      
      // Clear tick mark class from input groups
      const inputGroup = field.closest('.input-group');
      if (inputGroup) {
        inputGroup.classList.remove('input-group-filled-with-tick');
      }
      
      // Clear breed handler attribute so it can be reattached for this cat
      if (originalName === 'breed-unknown') {
        field.removeAttribute('data-breed-handler-attached');
      }
      
      // Update data attributes to track which cat this belongs to
      field.setAttribute('data-cat', catNumber);
      field.setAttribute('data-original-name', originalName);
    });

    // Update cat name displays
    stepElement.querySelectorAll('.cat-name-display').forEach(el => {
      el.textContent = `Cat ${catNumber}`;
    });
  }

  updateVetMessage(slideElement, fieldName, answerValue) {
    // Slide 3 has context-dependent vet messages (Wet vs Both)
    const step = slideElement.getAttribute('data-step');
    let vetMessage;

    if (step === '3') {
      const catNumber = parseInt(slideElement.getAttribute('data-cat')) || 1;
      const catData = this.submissionData[`cat${catNumber}`];
      const foodContext = catData ? catData.wet_dry_preference : null;

      // Show the matching context, hide the other
      const allVetMessages = slideElement.querySelectorAll('.vet-message[data-food-context]');
      allVetMessages.forEach(vm => { vm.style.display = 'none'; });

      if (foodContext) {
        vetMessage = slideElement.querySelector(`.vet-message[data-food-context="${foodContext}"]`);
        if (vetMessage) vetMessage.style.display = '';
      }
    } else {
      vetMessage = slideElement.querySelector('.vet-message');
    }

    if (!vetMessage) return;

    const bodyEl = vetMessage.querySelector('.vet-message__body');
    if (!bodyEl) return;

    const fieldMap = VET_OVERRIDE_KEYS[fieldName];
    if (!fieldMap) return;

    const datasetKey = fieldMap[answerValue];
    const overrideText = datasetKey ? vetMessage.dataset[datasetKey] : null;

    if (overrideText && overrideText.trim() !== '') {
      bodyEl.innerHTML = overrideText;
    } else {
      bodyEl.innerHTML = bodyEl.dataset.defaultText || '';
    }

    // Dynamic title swap
    const titleEl = vetMessage.querySelector('.vet-message__title');
    if (titleEl) {
      const titleMap = VET_TITLE_OVERRIDE_KEYS[fieldName];
      const titleKey = titleMap ? titleMap[answerValue] : null;
      const overrideTitle = titleKey ? vetMessage.dataset[titleKey] : null;

      if (overrideTitle && overrideTitle.trim() !== '') {
        titleEl.textContent = overrideTitle;
      } else {
        titleEl.textContent = titleEl.dataset.defaultTitle || '';
      }
    }

    // Pulse the vet message box to highlight the change
    vetMessage.classList.remove('vet-message--updated');
    void vetMessage.offsetWidth;
    vetMessage.classList.add('vet-message--updated');
  }

  syncVetMessages() {
    const activeSteps = document.querySelectorAll('.quiz-step.active');
    activeSteps.forEach(stepEl => {
      const step = stepEl.getAttribute('data-step');

      // For slide 3, show the correct food-context vet message even before a quantity is selected
      if (step === '3') {
        const catNumber = parseInt(stepEl.getAttribute('data-cat')) || 1;
        const catData = this.submissionData[`cat${catNumber}`];
        const foodContext = catData ? catData.wet_dry_preference : null;
        const allVetMessages = stepEl.querySelectorAll('.vet-message[data-food-context]');
        allVetMessages.forEach(vm => { vm.style.display = 'none'; });
        if (foodContext) {
          const matched = stepEl.querySelector(`.vet-message[data-food-context="${foodContext}"]`);
          if (matched) matched.style.display = '';
        }

        // Show/hide "both" notes on quantity options
        const bothNotes = stepEl.querySelectorAll('.radio-option__both-note');
        bothNotes.forEach(note => {
          note.style.display = foodContext === 'Both' ? '' : 'none';
        });

        // Swap label + subtitle to "both"-path variants when food pref = Both.
        // Empty-state safe: only replaces wet text when the `_both` field was
        // populated (the both span is only rendered by liquid when != blank).
        this.toggleQuantityBothLabels(stepEl, foodContext === 'Both');
      }

      // Check all checked radios and checkboxes that map to vet override fields
      Object.keys(VET_OVERRIDE_KEYS).forEach(fieldName => {
        const checkedInput = stepEl.querySelector(`input[name="${fieldName}"]:checked, input[data-original-name="${fieldName}"]:checked`);
        if (checkedInput) {
          this.updateVetMessage(stepEl, fieldName, checkedInput.value);
        }
      });
    });
  }

  handleMultiCatInput(input, name, catNumber) {
    let value = input.value;

    // Handle different input types
    if (input.type === 'checkbox') {
      // For checkboxes, find all checked boxes with the same original name and cat number
      let checkboxes;
      if (catNumber === 1) {
        // For cat 1, also check for plain name (single cat journey)
        checkboxes = document.querySelectorAll(`[data-original-name="${name}"][data-cat="${catNumber}"]:checked, [name="cat${catNumber}_${name}"]:checked, [name="${name}"]:checked`);
      } else {
        checkboxes = document.querySelectorAll(`[data-original-name="${name}"][data-cat="${catNumber}"]:checked, [name="cat${catNumber}_${name}"]:checked`);
      }
      value = Array.from(checkboxes).map(cb => cb.value);
    } else if (input.type === 'radio') {
      if (!input.checked) return;
    }

    // Update cat-specific data
    window.QuizState.updateCatData(catNumber, name, value);

    // Special handling for specific fields
    if (name === 'fuzzballer_name') {
      this.updateCatNameDisplaysForCat(value, catNumber);
    } else if (name === 'wet_dry_preference') {
      this.handleFoodPreferenceChange(value, catNumber);
    } else if (name === 'health_concerns_exists') {
      this.handleHealthConcernsChange(value, catNumber);
    } else if (name === 'health_concerns') {
      this.handleHealthConcernSelection(value, catNumber);
    }

    // Validate current step to update navigation
    const isValid = window.QuizValidators.validateStep(this.currentStep, this.submissionData);
    this.updateNavigationButtons(isValid);

    // Update vet message if this field has overrides
    const stepEl = input.closest('.quiz-step');
    if (stepEl) {
      this.updateVetMessage(stepEl, name, value);
    }
  }

  async confirmCatDeletion(catNumber) {
    const catData = this.submissionData[`cat${catNumber}`];
    const catName = catData?.fuzzballer_name || `Cat ${catNumber}`;

    if (await fuzzballConfirm(`This will delete all data for ${catName}.`, { title: `Remove ${catName}?`, confirmText: 'Remove' })) {
      this.deleteCat(catNumber);
    }
  }

  deleteCat(catNumber) {
    
    // Remove all steps for this cat
    for (let step = 1; step <= 5; step++) {
      const stepElement = document.querySelector(`[data-step="${step}"][data-cat="${catNumber}"]`);
      if (stepElement) {
        stepElement.remove();
      }
    }
    
    // Remove cat data and renumber remaining cats
    const catCount = this.submissionData.catCount || 1;
    
    // Shift data from higher numbered cats down
    for (let i = catNumber; i < catCount; i++) {
      if (this.submissionData[`cat${i + 1}`]) {
        this.submissionData[`cat${i}`] = this.submissionData[`cat${i + 1}`];
      }
    }
    
    // Remove the last cat's data
    delete this.submissionData[`cat${catCount}`];
    
    // Update cat count
    this.submissionData.catCount = catCount - 1;
    
    // Renumber DOM elements for remaining cats
    this.renumberCatsInDOM(catNumber);
    
    // Update add cat button visibility
    const addCatBtn = document.querySelector('.add-another-cat');
    if (addCatBtn && this.submissionData.catCount < 3) {
      addCatBtn.style.display = 'block';
    }
    
    // Save updated data
    window.QuizState.save('submissionData', this.submissionData);
    
    // Update display if we're on step 7
    if (this.currentStep === 7) {
      const recommendations = this.calculateAllCatsRecommendations();
      this.updateStep7Content(recommendations);
    }
    
    
    // Revalidate current step to update navigation buttons
    // After deletion, validation may now pass if all remaining cats have data
    const isValid = window.QuizValidators.validateStep(this.currentStep, this.submissionData);
    this.updateNavigationButtons(isValid);
  }

  renumberCatsInDOM(deletedCatNumber) {
    const catCount = this.submissionData.catCount;
    
    // For each cat after the deleted one, renumber its elements
    for (let oldNumber = deletedCatNumber + 1; oldNumber <= catCount + 1; oldNumber++) {
      const newNumber = oldNumber - 1;
      
      // Renumber all steps for this cat
      for (let step = 1; step <= 5; step++) {
        const stepElement = document.querySelector(`[data-step="${step}"][data-cat="${oldNumber}"]`);
        if (stepElement) {
          // Update cat number in data attribute
          stepElement.setAttribute('data-cat', newNumber);
          
          // Update cat number in headings
          const heading = stepElement.querySelector('h2, h3');
          if (heading) {
            heading.innerHTML = heading.innerHTML.replace(`Cat ${oldNumber}:`, `Cat ${newNumber}:`);
          }
          
          // Update field names and data attributes
          stepElement.querySelectorAll('input, select, textarea').forEach(field => {
            const oldName = field.name;
            const newName = oldName.replace(`cat${oldNumber}_`, `cat${newNumber}_`);
            field.name = newName;
            field.setAttribute('data-cat', newNumber);
          });
          
          // Update delete button if on step 1
          if (step === 1) {
            const deleteBtn = stepElement.querySelector('.delete-cat-btn');
            if (deleteBtn) {
              deleteBtn.setAttribute('data-cat', newNumber);
              // Update event listener
              const newDeleteBtn = deleteBtn.cloneNode(true);
              deleteBtn.parentNode.replaceChild(newDeleteBtn, deleteBtn);
              newDeleteBtn.addEventListener('click', () => this.confirmCatDeletion(newNumber));
            }
          }
        }
      }
    }
  }

  updateCatNameDisplaysForCat(name, catNumber) {
    const steps = document.querySelectorAll(`[data-cat="${catNumber}"]`);
    steps.forEach(step => {
      step.querySelectorAll('.cat-name-display').forEach(el => {
        el.textContent = name || `Cat ${catNumber}`;
      });
    });

    // Also update slide 6 headings with all cat names combined
    this.updateSlide6CatNames();
  }

  updateSlide6CatNames() {
    const catCount = this.submissionData.catCount || 1;
    const names = [];
    for (let i = 1; i <= catCount; i++) {
      const name = this.submissionData[`cat${i}`]?.fuzzballer_name;
      if (name) names.push(name);
    }
    const displayName = names.length > 0 ? names.join(' and ') : 'your cat';
    document.querySelectorAll('[data-step-visual="6"] .cat-name-display, [data-step-mobile="6"] .cat-name-display').forEach(el => {
      el.textContent = displayName;
    });
  }

  handleHealthConcernChange(input, catNumber) {
    const step = input.closest('.quiz-step');
    const healthDropdown = step.querySelector('.health-concerns-dropdown');
    const healthTip = step.querySelector('.health-tip-message');
    
    if (input.value === 'Yes') {
      if (healthDropdown) {
        healthDropdown.style.display = 'block';
        healthDropdown.querySelector('select').setAttribute('required', 'true');
      }
    } else {
      if (healthDropdown) {
        healthDropdown.style.display = 'none';
        healthDropdown.querySelector('select').removeAttribute('required');
        healthDropdown.querySelector('select').value = '';
      }
      if (healthTip) {
        healthTip.style.display = 'none';
      }
      window.QuizState.updateCatData(catNumber, 'health_concerns', '');
    }
  }

  getTrailBoxItemsCount(diet) {
    // Trail box goals based on diet preference. Derive numeric totals from
    // the live multi-pack size on the <trail-box> element so the item-count
    // chip rendered before the modal opens stays in sync with the customizer
    // setting (7 today → 14 wet / 8 both; 6 after the supplier swap → 12 wet
    // / 7 both). Dry-only stays 2 bags regardless.
    const multipackSize = this._getTrailBoxMultipackSize();
    switch(diet) {
      case 'Wet':
        return multipackSize * 2;     // cans-only goal: 2 × multipack
      case 'Dry':
        return 2;                     // 2 dry bags (size-independent)
      case 'Both':
        return multipackSize + 1;     // multipack cans + 1 dry bag
      default:
        return 2;                     // Default fallback
    }
  }

  /**
   * Read the section's trail-box multi-pack size off the <trail-box>
   * element's data attribute, with the same fallback rules as
   * TrailBoxBuilder.parseMultipackSize() so the controller derivations
   * match the storefront component's derivations.
   */
  _getTrailBoxMultipackSize(trailBoxElement) {
    const el = trailBoxElement || document.querySelector('trail-box');
    if (!el) return 7;
    const raw = el.getAttribute('data-trail-box-multipack-size');
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 14) {
      return parsed;
    }
    return 7;
  }

  /**
   * Parse a JSON product list attribute off the <trail-box> element
   * (data-trail-box-cans, data-trail-box-dry-foods). Each entry is
   * `{id, title, variant_id}`, matching how TrailBoxBuilder.loadProductsFromSettings
   * already reads the same attributes.
   */
  _getTrailBoxProducts(trailBoxElement, attribute) {
    if (!trailBoxElement) return [];
    const raw = trailBoxElement.getAttribute(attribute);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Spread `target` units across `products` as evenly as possible. Each
   * product gets `Math.floor(target / count)` units; the leftover
   * (`target % count`) gets distributed one extra to the first N products
   * in order. Returns `[{product, quantity}, …]`. Empty products list or
   * non-positive target → empty array.
   */
  _distributeEvenly(products, target) {
    if (!Array.isArray(products) || products.length === 0 || target <= 0) {
      return [];
    }
    const count = products.length;
    const base = Math.floor(target / count);
    let remainder = target - (base * count);
    return products.map(product => {
      const extra = remainder > 0 ? 1 : 0;
      if (remainder > 0) remainder -= 1;
      return { product, quantity: base + extra };
    });
  }

  updateSingleCatTrailBoxCount() {
    const trailBoxElement = document.querySelector('trail-box');
    // Default to the mixed goal (multipack cans + 1 dry) when no selections
    // or recommendations exist — matches today's 8 at multipack=7 and
    // scales to 7 at multipack=6.
    let totalItems = this._getTrailBoxMultipackSize(trailBoxElement) + 1;
    
    if (trailBoxElement && trailBoxElement.selections && trailBoxElement.selections.cat1) {
      totalItems = trailBoxElement.selections.cat1.reduce((sum, item) => sum + item.quantity, 0);
    } else {
      // Use default based on diet preference if trail box isn't ready
      const recommendations = this.lastCalculatedRecommendations;
      if (recommendations) {
        const catRec = recommendations.cats ? recommendations.cats.cat1 : recommendations;
        if (catRec && catRec.diet) {
          totalItems = this.getTrailBoxItemsCount(catRec.diet);
        }
      }
    }
    
    // Update all trail box count displays
    const allTrailBoxCounts = document.querySelectorAll('.trail-box-items-count');
    let updatedCount = 0;
    allTrailBoxCounts.forEach(element => {
      // Skip multi-cat elements and trail box summary header
      if (!element.closest('[data-cat]') && !element.closest('.trail-box-summary-header')) {
        element.textContent = totalItems;
        updatedCount++;
      }
    });
    
  }

  updateStep7MealPlanCounts() {
    // Update item counts for all cats based on bundle selections
    const bundleElement = document.querySelector('quiz-bundle');
    if (!bundleElement || !bundleElement.selections) return;
    
    
    // Update each cat's meal plan section
    Object.entries(bundleElement.selections).forEach(([catKey, catSelections]) => {
      // Extract cat number from catKey (e.g., "cat1" -> "1")
      const catNumber = catKey.replace('cat', '');
      const mealPlanSection = document.querySelector(`.meal-plan-section[data-cat="${catNumber}"]`);
      
      if (mealPlanSection) {
        const itemsCountEl = mealPlanSection.querySelector('.total-items-count');
        if (itemsCountEl && Array.isArray(catSelections)) {
          const totalItems = catSelections.reduce((sum, item) => sum + item.quantity, 0);
          itemsCountEl.textContent = totalItems;
        }
      }
    });
    
    // Also update the total bundle price
    const totalPrice = bundleElement.getTotalPrice();
    const bundlePriceEls = document.querySelectorAll('.bundle-total-price');
    bundlePriceEls.forEach(el => {
      el.textContent = `£${totalPrice.toFixed(2)}`;
    });

    // Bundle total changed — keep the free shipping bar mirroring it
    this.updateFreeShippingBar();
  }

  updateMultiCatTrailBoxCounts() {
    // Update trail box counts for each cat based on actual selections
    const trailBoxElement = document.querySelector('trail-box');
    if (!trailBoxElement || !trailBoxElement.selections) return;
    
    
    // Update each cat's trail box count
    Object.entries(trailBoxElement.selections).forEach(([catKey, catSelections]) => {
      const catNumber = catKey.replace('cat', '');
      const trailBoxSection = document.querySelector(`.trail-box-section[data-cat="${catNumber}"]`);
      
      if (trailBoxSection) {
        const countEl = trailBoxSection.querySelector('.trail-box-items-count');
        if (countEl && Array.isArray(catSelections)) {
          const totalItems = catSelections.reduce((sum, item) => sum + item.quantity, 0);
          countEl.textContent = totalItems;
        }
      }
    });
    
    // Update the global trail box count in delivery-info
    let totalTrailBoxItems = 0;
    Object.values(trailBoxElement.selections).forEach(catSelections => {
      if (Array.isArray(catSelections)) {
        totalTrailBoxItems += catSelections.reduce((sum, item) => sum + item.quantity, 0);
      }
    });
    
    const globalTrailBoxCount = document.querySelector('.delivery-info .trail-box-items-count');
    if (globalTrailBoxCount) {
      globalTrailBoxCount.textContent = totalTrailBoxItems;
    }
  }
  
  updateTrailBoxTotalItems() {
    // Calculate total trail box items across all cats
    let totalTrailBoxItems = 0;
    
    // Get all trail box sections
    const trailBoxSections = document.querySelectorAll('.trail-box-section');
    trailBoxSections.forEach(section => {
      const itemsCountEl = section.querySelector('.trail-box-items-count');
      if (itemsCountEl) {
        const items = parseInt(itemsCountEl.textContent) || 0;
        totalTrailBoxItems += items;
      }
    });
    
    // Update the global trail box items count in delivery-info
    const globalTrailBoxCount = document.querySelector('.delivery-info .trail-box-items-count');
    if (globalTrailBoxCount) {
      globalTrailBoxCount.textContent = totalTrailBoxItems;
    }
  }

  renderMultiCatResults(recommendations) {
    
    // Find the tab content containers
    const trialTabContent = document.querySelector('.tab-content[data-tab-content="trial"]');
    const monthlyTabContent = document.querySelector('.tab-content[data-tab-content="monthly"]');
    
    if (!trialTabContent || !monthlyTabContent) {
      return;
    }
    
    // Update trial tab total price (TRIAL_BOX_PRICE × number of cats + extras)
    const catCount = Object.keys(recommendations.cats).length;
    let totalTrialPrice = window.QuizConstants.FOOD.TRIAL_BOX_PRICE * catCount;
    
    // Add extras total to the trial tab price
    if (window.QuizExtrasManager) {
      const extrasTotal = window.QuizExtrasManager.getTotalPrice();
      totalTrialPrice += extrasTotal;
    }
    
    const trialPlanPrice = document.querySelector('.tab[data-tab="trial"] .plan-price');
    if (trialPlanPrice) {
      trialPlanPrice.textContent = `£${totalTrialPrice.toFixed(2)}`;
    }
    
    // Update checkout bar trial total
    const trialCheckoutTotal = document.querySelector('.trial-checkout-total');
    if (trialCheckoutTotal) {
      let finalTrialPrice = totalTrialPrice;
      
      // Add extras total from all cats
      if (window.QuizExtrasManager) {
        const extrasTotal = window.QuizExtrasManager.getTotalPrice(); // Gets total across all cats
        finalTrialPrice += extrasTotal;
      }
      
      trialCheckoutTotal.textContent = `£${finalTrialPrice.toFixed(2)}`;
    }
    
    // Update checkout bar for the active tab (trial is default)
    const activeTab = document.querySelector('.plan-tabs .tab.active');
    if (activeTab) {
      this.updateCheckoutBarForTab(activeTab.dataset.tab);
    } else {
      // Default to trial if no active tab found
      this.updateCheckoutBarForTab('trial');
    }
    
    // Clear existing single-cat sections
    const existingTrialSections = trialTabContent.querySelectorAll('.trail-box-section, .cat-container');
    const existingMonthlySections = monthlyTabContent.querySelectorAll('.meal-plan-section');
    existingTrialSections.forEach(el => el.remove());
    existingMonthlySections.forEach(el => el.remove());
    
    // Create sections for each cat
    Object.entries(recommendations.cats).forEach(([catKey, catRec]) => {
      // Extract cat number from key (e.g., 'cat1' -> 1)
      const catNumber = parseInt(catKey.replace('cat', ''));
      
      // Calculate prices for this cat — prefer live selections for this cat,
      // fall back to lower-bound estimate
      const trialBoxPrice = window.QuizConstants.FOOD.TRIAL_BOX_PRICE;
      const bundlePrice = this.getLiveOrEstimatedBundlePrice(catRec, catKey);
      
      // Show prices only when there are multiple cats
      const showPrices = Object.keys(recommendations.cats).length > 1;
      
      // Create trial box section for this cat
      const trialBoxHTML = `
        <div class="trail-box-section" data-cat="${catNumber}">
          <div class="trail-box-header">
            <h3 class="Md_Body_Bold">${catRec.catName}'s trial${showPrices ? ` - £${trialBoxPrice.toFixed(2)}` : ''}</h3>
            <div class="edit-icon-wrapper trail-box-edit toggle-trial-products">
              <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_28455_7529_trial)">
                  <path d="M12 13V16M12 16V19M12 16H15M12 16H9M21 16C21 20.9706 16.9706 25 12 25C7.02944 25 3 20.9706 3 16C3 11.0294 7.02944 7 12 7C16.9706 7 21 11.0294 21 16Z" stroke="#434548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_28455_7529_trial">
                    <rect width="24" height="24" fill="white" transform="translate(0 4)"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>

          <div class="trail-box-items-info">
            <span class="trail-box-items-count Md_Body_Bold">${this.getTrailBoxItemsCount(catRec.diet)}</span>
            <span class="S_Body_Regular"> items</span>
            <span class="edit-trail-box S_Body_Bold">Edit trial plan
              <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3H3C1.89543 3 1 3.89543 1 5V16C1 17.1046 1.89543 18 3 18H14C15.1046 18 16 17.1046 16 16V11M14.5858 1.58579C15.3668 0.804738 16.6332 0.804738 17.4142 1.58579C18.1953 2.36683 18.1953 3.63316 17.4142 4.41421L8.82842 13H6L6 10.1716L14.5858 1.58579Z" stroke="#434548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
          <div class="extras-inline-summary" style="display:none;">
            <span class="extras-count">0</span>
            <span class="S_Body_Regular"> extras</span>
            <span class="extras-total-price S_Body_Bold"></span>
          </div>

          <!-- Trial Products List -->
          <div class="trial-products-section">
            <h4 class="Md_Body_Bold products-list-header">Products in ${catRec.catName}'s trial</h4>
            <div class="unified-products-list" data-cat="${catNumber}">
              <!-- Dynamically populated -->
            </div>
          </div>
        </div>
      `;

      // Wrap in a container div
      const catContainerHTML = `
        <div class="cat-container" data-cat="${catNumber}">
          ${trialBoxHTML}
        </div>
      `;
      
      // Create meal plan section for this cat
      const mealPlanHTML = `
        <div class="meal-plan-section" data-cat="${catNumber}">
          <div class="meal-plan-header">
            <h3 class="Md_Body_Bold">${catRec.catName}'s meal plan${showPrices ? ` - £${bundlePrice.toFixed(2)}/month` : ''}</h3>
          </div>
          
          <div class="items-info">
            <span class="total-items-count Md_Body_Bold" data-cat-items="${catNumber}">0</span>
            <span class="S_Body_Regular"> items</span>
            <div class="edit-icon-wrapper toggle-monthly-products">
              <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_28455_7529)">
                  <path d="M12 13V16M12 16V19M12 16H15M12 16H9M21 16C21 20.9706 16.9706 25 12 25C7.02944 25 3 20.9706 3 16C3 11.0294 7.02944 7 12 7C16.9706 7 21 11.0294 21 16Z" stroke="#434548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_28455_7529">
                    <rect width="24" height="24" fill="white" transform="translate(0 4)"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
            <span class="edit-recipes S_Body_Bold">Edit monthly plan
              <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3H3C1.89543 3 1 3.89543 1 5V16C1 17.1046 1.89543 18 3 18H14C15.1046 18 16 17.1046 16 16V11M14.5858 1.58579C15.3668 0.804738 16.6332 0.804738 17.4142 1.58579C18.1953 2.36683 18.1953 3.63316 17.4142 4.41421L8.82842 13H6L6 10.1716L14.5858 1.58579Z" stroke="#434548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
          
          <!-- Monthly Products List -->
          <div class="monthly-products-section">
            <h4 class="Md_Body_Bold products-list-header">Products in ${catRec.catName}'s monthly plan</h4>
            <div class="monthly-products-list" data-cat="${catNumber}">
              <!-- Dynamically populated -->
            </div>
          </div>
        </div>
      `;
      
      // Insert sections
      trialTabContent.insertAdjacentHTML('beforeend', catContainerHTML);
      monthlyTabContent.insertAdjacentHTML('beforeend', mealPlanHTML);
    });
    
    // Update totals — prefer live bundle selections, fall back to estimate
    const totalBundlePrice = this.getLiveOrEstimatedBundlePrice(recommendations);
    window.QuizUtils.DOM.updateMultiple('.bundle-total-price', `£${totalBundlePrice.toFixed(2)}`);
    window.QuizUtils.DOM.updateMultiple('.bundle-system-price', `£${totalBundlePrice.toFixed(2)}`);
    
    // Update only the main total items display (not individual cat counts)
    const mainTotalItemsEl = document.querySelector('.tab-content[data-tab-content="monthly"] > .delivery-info .total-items-text');
    if (mainTotalItemsEl) {
      mainTotalItemsEl.textContent = `${recommendations.totals.bagsNeeded + recommendations.totals.packsNeeded} items in total`;
    }
    
    // Calculate total trail box items for all cats
    let totalTrailBoxItems = 0;
    Object.values(recommendations.cats).forEach(catRec => {
      totalTrailBoxItems += this.getTrailBoxItemsCount(catRec.diet);
    });
    
    // Update the global trail box items count in delivery-info
    const globalTrailBoxCount = document.querySelector('.delivery-info .trail-box-items-count');
    if (globalTrailBoxCount) {
      globalTrailBoxCount.textContent = totalTrailBoxItems;
    }
    
    // Update extras display for all cats
    if (window.QuizExtrasManager) {
      window.QuizExtrasManager.updateUI(); // This will update all cats
    }

    // Multi-cat re-render finished — refresh free shipping bar
    this.updateFreeShippingBar();
  }
};